import React, { useEffect, useMemo, useReducer, useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { Timestamp } from 'firebase/firestore';
import { chatQuizService } from '../services/chatQuizService';
import { ChatQuiz, ConnectedStudentInfo, StudentQuizResponse } from '../types/chatQuiz';
import { ClassMember } from '../types/alarm';
import { classCodeService } from '../services/classCodeService';

interface FlattenedResponse {
  studentId: string;
  response: StudentQuizResponse;
  index: number;
}

type ViewMode = 'students' | 'accuracy' | 'breakdown';

interface ViewState {
  viewMode: ViewMode;
  sortOrder: 'number' | 'response' | 'random' | 'seat';
  accuracySortOrder: 'response' | 'alphabetical';
  breakdownSortOrder: 'correctness' | 'response' | 'alphabetical' | 'count';
}

type ViewStateAction =
  | { type: 'SET_VIEW_MODE'; viewMode: ViewMode; storedSortOrder?: 'number' | 'response' | 'random' | 'seat'; storedAccuracySortOrder?: 'response' | 'alphabetical'; storedBreakdownSortOrder?: 'correctness' | 'response' | 'alphabetical' | 'count' }
  | { type: 'SET_SORT_ORDER'; sortOrder: 'number' | 'response' | 'random' | 'seat' }
  | { type: 'SET_ACCURACY_SORT_ORDER'; accuracySortOrder: 'response' | 'alphabetical' }
  | { type: 'SET_BREAKDOWN_SORT_ORDER'; breakdownSortOrder: 'correctness' | 'response' | 'alphabetical' | 'count' }
  | { type: 'RESTORE_FROM_STORAGE'; classCode: string; quizId: string };

const PresentationPage: React.FC = () => {
  const searchParams = useMemo(() => new URLSearchParams(window.location.search), []);
  const classCode = searchParams.get('classCode') || '';
  const quizId = searchParams.get('quizId') || '';
  const setId = searchParams.get('setId') || '';
  const memberGroup = searchParams.get('memberGroup') || undefined;
  const isSetPresentation = (searchParams.get('mode') || '') === 'set' || !!setId;
  const initialResponseVisibility = (searchParams.get('responses') || 'public') !== 'private';
  const initialNameVisibility = (searchParams.get('names') || 'public') !== 'private';
  const initialAnswerVisibility = (searchParams.get('answers') || 'public') !== 'private';
  const initialCorrectRateVisibility = (searchParams.get('correctRate') || 'public') !== 'private';
  const initialScoreVisibility = (searchParams.get('score') || '') === 'public';

  const [quiz, setQuiz] = useState<ChatQuiz | null>(null);
  const [setTitle, setSetTitle] = useState<string>('');
  const [setProgress, setSetProgress] = useState<
    Record<string, { answered: number; correct: number; essay: number; total: number }>
  >({});
  const [isSetLoading, setIsSetLoading] = useState<boolean>(isSetPresentation);
  const [setSortOrder, setSetSortOrder] = useState<'number' | 'response' | 'correct' | 'score' | 'seat'>('response');
  const [responses, setResponses] = useState<{
    [studentId: string]: StudentQuizResponse | StudentQuizResponse[];
  }>({});
  const [connectedStudents, setConnectedStudents] = useState<{ [studentId: string]: ConnectedStudentInfo }>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [foundSetId, setFoundSetId] = useState<string | null>(null);
  const [classMembersMap, setClassMembersMap] = useState<{ [studentId: string]: ClassMember }>({});
  const [isResponsePublic, setIsResponsePublic] = useState(initialResponseVisibility);
  const [isNamePublic, setIsNamePublic] = useState(initialNameVisibility);
  const [isAnswerPublic, setIsAnswerPublic] = useState(initialAnswerVisibility);
  const [isCorrectRateVisible, setIsCorrectRateVisible] = useState(initialCorrectRateVisibility);
  const [isScoreVisible, setIsScoreVisible] = useState(initialScoreVisibility);
  const [themeMode, setThemeMode] = useState<'dark' | 'light'>(() => {
    if (typeof window !== 'undefined') {
      const stored = window.localStorage.getItem('app-theme-mode');
      if (stored === 'dark' || stored === 'light') return stored;
    }
    return 'dark';
  });
  const [showQuizDetailModal, setShowQuizDetailModal] = useState(false);
  
  // localStorage에서 블럭 크기 복원
  const getStoredBlockSize = (): number => {
    try {
      const stored = localStorage.getItem('presentation_blockSize');
      if (stored) {
        const parsed = parseFloat(stored);
        // 유효한 범위(0.7 ~ 2.5) 내의 값만 반환
        if (!isNaN(parsed) && parsed >= 0.7 && parsed <= 2.5) {
          return parsed;
        }
      }
    } catch {
      // localStorage 읽기 실패 시 기본값 반환
    }
    return 1.0; // 기본값
  };
  
  const [blockSize, setBlockSize] = useState(getStoredBlockSize()); // 블럭 크기 배율 (0.7 ~ 2.5)

  const handleThemeToggle = useCallback(() => {
    setThemeMode((prev) => {
      const next = prev === 'dark' ? 'light' : 'dark';
      if (typeof document !== 'undefined') document.documentElement.dataset.theme = next;
      if (typeof window !== 'undefined') window.localStorage.setItem('app-theme-mode', next);
      return next;
    });
  }, []);
  const [showWebcam, setShowWebcam] = useState(false);
  const [availableCameras, setAvailableCameras] = useState<MediaDeviceInfo[]>([]);
  const [, setCurrentCameraIndex] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const currentCameraIndexRef = useRef(0);
  const availableCamerasRef = useRef<MediaDeviceInfo[]>([]);

  // 카메라 목록 가져오기
  const getCameraList = useCallback(async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const cameras = devices.filter(device => device.kind === 'videoinput');
      setAvailableCameras(cameras);
      availableCamerasRef.current = cameras;
      return cameras;
    } catch (error) {
      console.error('카메라 목록 가져오기 실패:', error);
      return [];
    }
  }, []);

  // 웹캠 시작 함수 (특정 카메라 ID로 시작 가능)
  const startWebcam = useCallback(async (deviceId?: string) => {
    try {
      // 기존 스트림 중지
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }

      const constraints: MediaStreamConstraints = {
        video: deviceId 
          ? { deviceId: { exact: deviceId }, width: 1280, height: 720 }
          : { width: 1280, height: 720 },
        audio: false
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      
      // 비디오 요소에 즉시 스트림 연결 (카메라 전환 시에도 작동하도록)
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play().catch(err => {
          console.error('비디오 재생 실패:', err);
        });
      }
      
      // 카메라 목록 업데이트
      const cameras = await getCameraList();
      
      // 현재 사용 중인 카메라의 인덱스 찾기
      if (deviceId) {
        const index = cameras.findIndex(cam => cam.deviceId === deviceId);
        if (index !== -1) {
          setCurrentCameraIndex(index);
          currentCameraIndexRef.current = index;
        }
      }
      
      setShowWebcam(true);
    } catch (error) {
      console.error('웹캠 접근 실패:', error);
      alert('웹캠에 접근할 수 없습니다. 카메라 권한을 확인해주세요.');
    }
  }, [getCameraList]);

  // 웹캠 중지 함수
  const stopWebcam = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    
    setShowWebcam(false);
    setCurrentCameraIndex(0);
    currentCameraIndexRef.current = 0;
  }, []);

  // 카메라 전환 함수 (ref를 사용하여 항상 최신 값 참조)
  const switchCamera = useCallback(async () => {
    const cameras = availableCamerasRef.current;
    if (cameras.length < 2) return;
    
    const nextIndex = (currentCameraIndexRef.current + 1) % cameras.length;
    const nextCamera = cameras[nextIndex];
    
    if (nextCamera?.deviceId) {
      // 먼저 인덱스 업데이트 (다음 전환을 위해)
      currentCameraIndexRef.current = nextIndex;
      setCurrentCameraIndex(nextIndex);
      await startWebcam(nextCamera.deviceId);
    }
  }, [startWebcam]);

  // showWebcam이 true가 되면 비디오 요소에 스트림 연결
  useEffect(() => {
    if (!showWebcam || !streamRef.current) {
      if (videoRef.current && !showWebcam) {
        videoRef.current.srcObject = null;
      }
      return;
    }

    // 비디오 요소가 DOM에 렌더링될 때까지 대기
    const timer = setTimeout(() => {
      const video = videoRef.current;
      const stream = streamRef.current;
      
      if (!video || !stream) return;

      // 비디오 메타데이터 로드 후 재생
      const handleLoadedMetadata = () => {
        if (video) {
          video.play().catch(err => {
            console.error('비디오 재생 실패:', err);
          });
        }
      };
      
      video.addEventListener('loadedmetadata', handleLoadedMetadata);
      video.srcObject = stream;
      
      // 이미 로드된 경우 즉시 재생 시도
      if (video.readyState >= 1) {
        video.play().catch(err => {
          console.error('비디오 재생 실패:', err);
        });
      }
      
      // 정리 함수를 위한 참조 저장
      return () => {
        if (video) {
          video.removeEventListener('loadedmetadata', handleLoadedMetadata);
        }
      };
    }, 150);
    
    return () => {
      clearTimeout(timer);
    };
  }, [showWebcam]);

  // 카메라 토글 함수
  const toggleWebcam = useCallback(() => {
    if (showWebcam) {
      stopWebcam();
    } else {
      startWebcam();
    }
  }, [showWebcam, startWebcam, stopWebcam]);

  // 모달 닫을 때 웹캠도 중지
  const handleCloseModal = useCallback(() => {
    stopWebcam();
    setShowQuizDetailModal(false);
  }, [stopWebcam]);

  // 창 닫기 함수
  const handleCloseWindow = useCallback(() => {
    window.close();
    // window.close()가 작동하지 않는 경우 (일부 브라우저에서)
    // 사용자에게 알림을 표시할 수 있습니다
    if (!document.hidden) {
      // 창이 닫히지 않았으면 사용자에게 수동으로 닫으라고 안내
      // (선택사항)
    }
  }, []);

  // 마운트 시 document 테마와 동기화
  useEffect(() => {
    const current = document.documentElement.dataset.theme;
    if (current === 'light' || current === 'dark') setThemeMode(current);
  }, []);

  // 프레젠테이션 페이지 타이틀 설정
  useEffect(() => {
    document.title = '에듀코디-프레젠테이션';
    
    // 컴포넌트 언마운트 시 원래 제목으로 복원
    return () => {
      document.title = '에듀코디';
    };
  }, []);

  // localStorage에서 정렬 상태 복원
  const getStoredSortOrder = useCallback((key: string, defaultValue: string): string => {
    if (!classCode || !quizId) {
      return defaultValue;
    }
    try {
      const stored = localStorage.getItem(`presentation_sort_${classCode}_${quizId}_${key}`);
      return stored || defaultValue;
    } catch {
      return defaultValue;
    }
  }, [classCode, quizId]);

  // 정렬 상태를 localStorage에 저장
  const saveSortOrder = useCallback((key: string, value: string) => {
    if (!classCode || !quizId) {
      return;
    }
    try {
      localStorage.setItem(`presentation_sort_${classCode}_${quizId}_${key}`, value);
    } catch {
      // localStorage 저장 실패 시 무시
    }
  }, [classCode, quizId]);

  // viewState reducer
  const viewStateReducer = (state: ViewState, action: ViewStateAction): ViewState => {
    switch (action.type) {
      case 'SET_VIEW_MODE': {
        // 현재 뷰의 정렬 상태 저장
        if (state.viewMode === 'accuracy') {
          saveSortOrder('accuracy', state.accuracySortOrder);
        } else if (state.viewMode === 'breakdown') {
          saveSortOrder('breakdown', state.breakdownSortOrder);
        } else if (state.viewMode === 'students') {
          saveSortOrder('students', state.sortOrder);
        }

        // 액션에 포함된 저장된 값을 사용 (없으면 현재 값 유지)
        const newSortOrder = action.storedSortOrder ?? state.sortOrder;
        const newAccuracySortOrder = action.storedAccuracySortOrder ?? state.accuracySortOrder;
        const newBreakdownSortOrder = action.storedBreakdownSortOrder ?? state.breakdownSortOrder;

        // viewMode와 정렬 상태를 동시에 업데이트 (원자적 업데이트)
        return {
          viewMode: action.viewMode,
          sortOrder: newSortOrder,
          accuracySortOrder: newAccuracySortOrder,
          breakdownSortOrder: newBreakdownSortOrder,
        };
      }
      case 'SET_SORT_ORDER':
        return { ...state, sortOrder: action.sortOrder };
      case 'SET_ACCURACY_SORT_ORDER':
        return { ...state, accuracySortOrder: action.accuracySortOrder };
      case 'SET_BREAKDOWN_SORT_ORDER':
        return { ...state, breakdownSortOrder: action.breakdownSortOrder };
      case 'RESTORE_FROM_STORAGE': {
        const storedSortOrder = getStoredSortOrder('students', 'response');
        const storedAccuracySortOrder = getStoredSortOrder('accuracy', 'response');
        const storedBreakdownSortOrder = getStoredSortOrder('breakdown', 'response');
        return {
          ...state,
          sortOrder: (['number', 'response', 'random', 'seat'].includes(storedSortOrder) ? storedSortOrder : 'response') as 'number' | 'response' | 'random' | 'seat',
          accuracySortOrder: (['response', 'alphabetical'].includes(storedAccuracySortOrder) ? storedAccuracySortOrder : 'response') as 'response' | 'alphabetical',
          breakdownSortOrder: (['correctness', 'response', 'alphabetical', 'count'].includes(storedBreakdownSortOrder) ? storedBreakdownSortOrder : 'response') as 'correctness' | 'response' | 'alphabetical' | 'count',
        };
      }
      default:
        return state;
    }
  };

  const [viewState, dispatchViewState] = useReducer(viewStateReducer, {
    viewMode: 'students',
    sortOrder: getStoredSortOrder('students', 'response') as 'number' | 'response' | 'random' | 'seat',
    accuracySortOrder: getStoredSortOrder('accuracy', 'response') as 'response' | 'alphabetical',
    breakdownSortOrder: getStoredSortOrder('breakdown', 'response') as 'correctness' | 'response' | 'alphabetical' | 'count',
  });

  const { viewMode, sortOrder, accuracySortOrder, breakdownSortOrder } = viewState;

  // classCode와 quizId가 준비되면 localStorage에서 정렬 상태 복원
  useEffect(() => {
    if (classCode && quizId) {
      dispatchViewState({ type: 'RESTORE_FROM_STORAGE', classCode, quizId });
    }
  }, [classCode, quizId]); // 초기 로드 시 한 번만 실행

  // viewMode 변경 핸들러 (localStorage에서 값을 먼저 읽어서 깜박임 방지)
  const handleViewModeChange = (newViewMode: ViewMode) => {
    // localStorage에서 새 뷰의 정렬 상태를 미리 읽어옴
    let storedSortOrder: 'number' | 'response' | 'random' | 'seat' | undefined;
    let storedAccuracySortOrder: 'response' | 'alphabetical' | undefined;
    let storedBreakdownSortOrder: 'correctness' | 'response' | 'alphabetical' | 'count' | undefined;

    if (classCode && quizId) {
      if (newViewMode === 'accuracy') {
        const stored = getStoredSortOrder('accuracy', 'response');
        if (['response', 'alphabetical'].includes(stored)) {
          storedAccuracySortOrder = stored as 'response' | 'alphabetical';
        }
      } else if (newViewMode === 'breakdown') {
        const stored = getStoredSortOrder('breakdown', 'response');
        if (['correctness', 'response', 'alphabetical', 'count'].includes(stored)) {
          storedBreakdownSortOrder = stored as 'correctness' | 'response' | 'alphabetical' | 'count';
        }
      } else if (newViewMode === 'students') {
        const stored = getStoredSortOrder('students', 'response');
        if (['number', 'response', 'random', 'seat'].includes(stored)) {
          storedSortOrder = stored as 'number' | 'response' | 'random' | 'seat';
        }
      }
    }

    // 저장된 값을 액션에 포함하여 디스패치 (원자적 업데이트)
    dispatchViewState({
      type: 'SET_VIEW_MODE',
      viewMode: newViewMode,
      storedSortOrder,
      storedAccuracySortOrder,
      storedBreakdownSortOrder,
    });
  };

  // 정렬 상태 변경 시 localStorage에 저장
  useEffect(() => {
    if (isSetPresentation) return;
    if (classCode && quizId) {
      saveSortOrder('students', sortOrder);
    }
  }, [sortOrder, classCode, quizId, isSetPresentation, saveSortOrder]);

  useEffect(() => {
    if (isSetPresentation) return;
    if (classCode && quizId) {
      saveSortOrder('accuracy', accuracySortOrder);
    }
  }, [accuracySortOrder, classCode, quizId, isSetPresentation, saveSortOrder]);

  useEffect(() => {
    if (isSetPresentation) return;
    if (classCode && quizId) {
      saveSortOrder('breakdown', breakdownSortOrder);
    }
  }, [breakdownSortOrder, classCode, quizId, isSetPresentation, saveSortOrder]);

  // 이름 비공개일 때 번호순이 선택되어 있으면 응답순으로 변경
  useEffect(() => {
    if (!isNamePublic && sortOrder === 'number') {
      dispatchViewState({ type: 'SET_SORT_ORDER', sortOrder: 'response' });
    }
  }, [isNamePublic, sortOrder]);

  useEffect(() => {
    if (isSetPresentation) return;
    if (!classCode || !quizId) {
      setError('클래스 코드 또는 퀴즈 정보가 누락되었습니다.');
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const loadInitialQuiz = async () => {
      try {
        let quizData: ChatQuiz | null = null;
        let targetSetId: string | null = null;
        
        // setId가 있으면 해당 세트에서 바로 찾기
        if (setId) {
          targetSetId = setId;
          const setData = await chatQuizService.getQuizSetById(classCode, setId);
          if (setData && setData.quizzes && setData.quizzes[quizId]) {
            const quizInSet = setData.quizzes[quizId] as any;
            // normalizeQuizData와 동일한 로직으로 데이터 변환
            const rawCreatedAt = quizInSet.createdAt;
            let createdAt: Timestamp;
            if (rawCreatedAt instanceof Timestamp) {
              createdAt = rawCreatedAt;
            } else if (rawCreatedAt instanceof Date) {
              createdAt = Timestamp.fromDate(rawCreatedAt);
            } else if (rawCreatedAt && typeof rawCreatedAt === 'object' && 'seconds' in rawCreatedAt && 'nanoseconds' in rawCreatedAt) {
              createdAt = new Timestamp(rawCreatedAt.seconds, rawCreatedAt.nanoseconds);
            } else {
              createdAt = Timestamp.now();
            }

            const rawUpdatedAt = quizInSet.updatedAt;
            let updatedAt: Timestamp | null = null;
            if (rawUpdatedAt instanceof Timestamp) {
              updatedAt = rawUpdatedAt;
            } else if (rawUpdatedAt instanceof Date) {
              updatedAt = Timestamp.fromDate(rawUpdatedAt);
            } else if (rawUpdatedAt && typeof rawUpdatedAt === 'object' && 'seconds' in rawUpdatedAt && 'nanoseconds' in rawUpdatedAt) {
              updatedAt = new Timestamp(rawUpdatedAt.seconds, rawUpdatedAt.nanoseconds);
            }

            quizData = {
              id: quizId,
              type: quizInSet.type,
              question: quizInSet.question || '',
              questionImageUrl: quizInSet.questionImageUrl || '',
              choices: quizInSet.choices || [],
              correctAnswers: quizInSet.correctAnswers || [],
              answers: quizInSet.answers || [],
              guide: quizInSet.guide || '',
              createdAt,
              updatedAt: updatedAt || createdAt,
              order: quizInSet.order ?? 0,
              defaultMultiAnswerMode: quizInSet.defaultMultiAnswerMode ?? false,
              defaultPublicMode: quizInSet.defaultPublicMode ?? false,
              responses: quizInSet.responses || {},
              answeredStudents: quizInSet.answeredStudents || [],
            };
          }
        } else {
          // setId가 없으면 모든 세트를 검색 (main 우선하지 않음)
          const allSets = await chatQuizService.listQuizSets(classCode);
          for (const set of allSets) {
            if (set.quizzes && set.quizzes[quizId]) {
              targetSetId = set.id;
              const quizInSet = set.quizzes[quizId] as any;
              // normalizeQuizData와 동일한 로직으로 데이터 변환
              const rawCreatedAt = quizInSet.createdAt;
              let createdAt: Timestamp;
              if (rawCreatedAt instanceof Timestamp) {
                createdAt = rawCreatedAt;
              } else if (rawCreatedAt instanceof Date) {
                createdAt = Timestamp.fromDate(rawCreatedAt);
              } else if (rawCreatedAt && typeof rawCreatedAt === 'object' && 'seconds' in rawCreatedAt && 'nanoseconds' in rawCreatedAt) {
                createdAt = new Timestamp(rawCreatedAt.seconds, rawCreatedAt.nanoseconds);
              } else {
                createdAt = Timestamp.now();
              }

              const rawUpdatedAt = quizInSet.updatedAt;
              let updatedAt: Timestamp | null = null;
              if (rawUpdatedAt instanceof Timestamp) {
                updatedAt = rawUpdatedAt;
              } else if (rawUpdatedAt instanceof Date) {
                updatedAt = Timestamp.fromDate(rawUpdatedAt);
              } else if (rawUpdatedAt && typeof rawUpdatedAt === 'object' && 'seconds' in rawUpdatedAt && 'nanoseconds' in rawUpdatedAt) {
                updatedAt = new Timestamp(rawUpdatedAt.seconds, rawUpdatedAt.nanoseconds);
              }

              quizData = {
                id: quizId,
                type: quizInSet.type,
                question: quizInSet.question || '',
                questionImageUrl: quizInSet.questionImageUrl || '',
                choices: quizInSet.choices || [],
                correctAnswers: quizInSet.correctAnswers || [],
                answers: quizInSet.answers || [],
                guide: quizInSet.guide || '',
                createdAt,
                updatedAt: updatedAt || createdAt,
                order: quizInSet.order ?? 0,
                defaultMultiAnswerMode: quizInSet.defaultMultiAnswerMode ?? false,
                defaultPublicMode: quizInSet.defaultPublicMode ?? false,
                responses: quizInSet.responses || {},
                answeredStudents: quizInSet.answeredStudents || [],
              };
              break;
            }
          }
        }
        
        if (!cancelled) {
          if (quizData) {
            setQuiz(quizData);
            setFoundSetId(targetSetId);
          } else {
            setError('퀴즈 정보를 찾을 수 없습니다.');
          }
        }
      } catch (err) {
        if (!cancelled) {
          console.error('❌ 프레젠테이션 퀴즈 로드 실패:', err);
          setError('퀴즈 정보를 불러오는 중 오류가 발생했습니다.');
        }
      }
    };

    loadInitialQuiz();

    const unsubscribeActiveQuiz = chatQuizService.listenToActiveQuiz(
      classCode,
      (nextQuiz, activeId, _multi, _publicMode, metaInfo) => {
        if (cancelled) {
          return;
        }
        if (nextQuiz && nextQuiz.id === quizId) {
          setQuiz(nextQuiz);
        }
        if (metaInfo?.connectedStudents) {
          setConnectedStudents(metaInfo.connectedStudents);
        }
      }
    );

    return () => {
      cancelled = true;
      unsubscribeActiveQuiz();
    };
  }, [classCode, quizId, isSetPresentation, setId]);

  // foundSetId가 설정되면 응답 리스너 설정
  useEffect(() => {
    if (isSetPresentation) return;
    if (!classCode || !quizId) return;

    // setId가 있으면 바로 사용, 없으면 foundSetId 사용
    const targetSetId = setId || foundSetId;

    let cancelled = false;

    console.log(`🔍 응답 리스너 설정: classCode=${classCode}, quizId=${quizId}, targetSetId=${targetSetId}`);
    
    const unsubscribeResponses = chatQuizService.listenToResponses(
      classCode,
      quizId,
      (nextResponses) => {
        console.log(`📥 응답 콜백 호출됨:`, { responseCount: Object.keys(nextResponses || {}).length, responses: nextResponses });
        if (!cancelled) {
          setResponses(nextResponses || {});
          setIsLoading(false);
          console.log(`✅ 응답 상태 업데이트 완료, isLoading=false`);
        }
      },
      targetSetId || undefined
    );

    return () => {
      cancelled = true;
      unsubscribeResponses();
    };
  }, [classCode, quizId, isSetPresentation, setId, foundSetId]);

  useEffect(() => {
    if (!classCode) {
      return;
    }
    let cancelled = false;

    const loadMembers = async () => {
      try {
        const location = await classCodeService.getClassLocationByCode(classCode);
        if (!location) {
          return;
        }
        
        // 새 컬렉션 구조(ClassMembersProfile)를 사용하도록 변경
        // 현재 선택된 멤버 그룹의 모든 파티션 데이터를 로드
        const { ClassMembersPartitionService } = await import('../services/classMembersPartitionService');
        const partitionService = new ClassMembersPartitionService(location.userId, location.classId, memberGroup);
        const members = await partitionService.loadAllMembers();
        
        const map: { [studentId: string]: ClassMember } = {};
        members.forEach((member) => {
          if (member?.id) {
            map[member.id] = member;
          }
        });
        
        if (!cancelled) {
          setClassMembersMap(map);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('❌ 프레젠테이션 멤버 목록 로드 실패:', err);
        }
      }
    };

    loadMembers();

    return () => {
      cancelled = true;
    };
  }, [classCode, memberGroup]);

  // 자리배치 데이터 state
  const [seatArrangementData, setSeatArrangementData] = useState<any[] | null>(null);
  const [seatArrangementError, setSeatArrangementError] = useState<string>('');
  const [isSeatRotated, setIsSeatRotated] = useState<boolean>(false);

  // 자리 배치 데이터 로드
  const loadSeatArrangementData = useCallback(async () => {
    if (!classCode) {
      setSeatArrangementError('클래스 코드가 없습니다.');
      return;
    }

    try {
      const location = await classCodeService.getClassLocationByCode(classCode);
      if (!location) {
        setSeatArrangementError('클래스 정보를 찾을 수 없습니다.');
        return;
      }

      const { ClassMembersPartitionService } = await import('../services/classMembersPartitionService');
      
      // 현재 선택된 멤버 그룹의 모든 파티션 데이터를 로드
      const partitionService = new ClassMembersPartitionService(location.userId, location.classId, memberGroup);
      const members = await partitionService.loadAllMembers();

      if (members.length === 0) {
        setSeatArrangementError('자리배치를 먼저 해주세요');
        setSeatArrangementData(null);
        return;
      }
      
      // 현재 월에 해당하는 자리 배치 찾기
      const currentDate = new Date();
      const currentMonth = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
      
      // 자리 배치 데이터가 있는 월들 찾기
      const availableMonths = new Set<string>();
      members.forEach((member: any) => {
        if (member.seatPositions && typeof member.seatPositions === 'object') {
          Object.keys(member.seatPositions).forEach((month: string) => {
            availableMonths.add(month);
          });
        }
      });

      let selectedMonth = currentMonth;
      
      // 현재 월이 없으면 가장 최근 월 선택
      if (!availableMonths.has(currentMonth)) {
        const sortedMonths = Array.from(availableMonths).sort().reverse();
        if (sortedMonths.length === 0) {
          setSeatArrangementError('자리배치를 먼저 해주세요');
          setSeatArrangementData(null);
          return;
        }
        selectedMonth = sortedMonths[0];
      }

      // 선택된 월의 자리 배치 데이터 추출
      const seatData = Object.values(classMembersMap).map((member) => {
        const memberData = members.find((m: any) => m.id === member.id);
        const seatPosition = memberData?.seatPositions?.[selectedMonth];
        
        return {
          studentId: member.id,
          name: member.name || '이름 없음',
          number: member.number || '',
          seatRow: seatPosition?.row || 0,
          seatCol: seatPosition?.col || 0,
          profileImage: member.profileImage,
        };
      }).filter((seat) => seat.seatRow !== 0 || seat.seatCol !== 0); // 자리가 설정된 멤버만

      setSeatArrangementData(seatData);
      setSeatArrangementError('');
    } catch (error) {
      console.error('❌ 자리 배치 데이터 로드 실패:', error);
      setSeatArrangementError('자리배치를 먼저 해주세요');
      setSeatArrangementData(null);
    }
  }, [classCode, classMembersMap, memberGroup]);

  // sortOrder가 'seat'일 때 자리배치 데이터 로드 (단일 퀴즈)
  useEffect(() => {
    const { sortOrder } = viewState;
    if (sortOrder === 'seat' && Object.keys(classMembersMap).length > 0) {
      loadSeatArrangementData();
    }
  }, [viewState, classMembersMap, loadSeatArrangementData]);

  // 세트 프레젠테이션에서 setSortOrder가 'seat'일 때 자리배치 데이터 로드
  useEffect(() => {
    if (!isSetPresentation || setSortOrder !== 'seat' || Object.keys(classMembersMap).length === 0) return;
    loadSeatArrangementData();
  }, [isSetPresentation, setSortOrder, classMembersMap, loadSeatArrangementData]);

  // 세트 프레젠테이션용 진행도 로드
  useEffect(() => {
    if (!isSetPresentation) return;
    if (!classCode || !setId) {
      setError('클래스 코드 또는 문제 세트 정보가 누락되었습니다.');
      setIsSetLoading(false);
      return;
    }

    let cancelled = false;

    const loadSetProgress = async () => {
      try {
        const setDoc = await chatQuizService.getQuizSetById(classCode, setId);
        if (cancelled) return;
        if (!setDoc) {
          setError('문제 세트 정보를 불러올 수 없습니다.');
          setIsSetLoading(false);
          return;
        }
        setSetTitle(setDoc.title || '');
        const quizzes = setDoc.quizzes || {};
        const total = Object.keys(quizzes).length;
        const progress: Record<string, { answered: number; correct: number; essay: number; total: number }> = {};

        Object.values(quizzes).forEach((quiz: any) => {
          const responses = quiz?.responses || {};
          const quizType = quiz?.type;
          Object.entries(responses).forEach(([studentId, resp]) => {
            const arr = Array.isArray(resp) ? resp : [resp];
            const hasAnyAnswer = arr.some((r: any) => !!r?.answer || !!r?.submittedAt);
            if (!hasAnyAnswer) return;

            const isEssay = quizType === 'essay';
            const hasCorrect = arr.some((r: any) => r?.isCorrect === true);

            if (!progress[studentId]) {
              progress[studentId] = { answered: 0, correct: 0, essay: 0, total };
            }
            progress[studentId].answered += 1;
            if (isEssay) {
              progress[studentId].essay += 1;
            } else if (hasCorrect) {
              progress[studentId].correct += 1;
            }
          });
        });

        setSetProgress(progress);
      } catch (err) {
        if (!cancelled) {
          console.error('❌ 세트 프레젠테이션 데이터 로드 실패:', err);
          setError('문제 세트 정보를 불러오는 중 오류가 발생했습니다.');
        }
      } finally {
        if (!cancelled) {
          setIsSetLoading(false);
        }
      }
    };

    loadSetProgress();

    return () => {
      cancelled = true;
    };
  }, [isSetPresentation, classCode, setId]);

  const getDisplayName = useCallback((
    studentId: string,
    response: StudentQuizResponse,
    entryIndex: number
  ) => {
    if (!isNamePublic) {
      return `익명 ${entryIndex + 1}`;
    }

    const memberInfo = classMembersMap[studentId];
    const info = connectedStudents[studentId];
    if (info?.isGuest) {
      return response.guestName || info.name || '게스트';
    }

    const namePart = memberInfo?.name || info?.name || response.guestName || `멤버 ${studentId.slice(-4)}`;
    const number = memberInfo?.number ?? info?.number;
    if (number !== undefined && number !== null && String(number).trim() !== '') {
      return `${number}. ${namePart}`;
    }
    return namePart;
  }, [isNamePublic, classMembersMap, connectedStudents]);

  const getNumberInfo = useCallback((studentId: string, response: StudentQuizResponse) => {
    const info = connectedStudents[studentId];
    if (info?.isGuest || response.guestName) {
      return { label: 'G', isGuest: true };
    }

    const memberInfo = classMembersMap[studentId];
    if (memberInfo?.number) {
      return { label: memberInfo.number, isGuest: false };
    }

    if (info?.number) {
      return { label: info.number, isGuest: false };
    }

    return { label: null, isGuest: false };
  }, [connectedStudents, classMembersMap]);

  const setProgressList = useMemo(() => {
    if (!isSetPresentation) return [];
    const entries = Object.entries(setProgress || {}).map(([studentId, stats]) => {
      const member = classMembersMap[studentId];
      const number = member?.number || '';
      const namePart = member?.name || `멤버 ${studentId.slice(-4)}`;
      const displayName = isNamePublic
        ? (number ? `${number}. ${namePart}` : namePart)
        : '익명';
      const total = stats.total || 0;
      const answered = stats.answered || 0;
      const responseRate = total > 0 ? answered / total : 0;
      const correctRate = total > 0 ? (stats.correct + stats.essay) / total : 0;
      const correctRateByAnswered = answered > 0 ? (stats.correct + stats.essay) / answered : 0;
      const score = total > 0 ? Math.round(((stats.correct + stats.essay) / total) * 100) : 0;
      return { studentId, number, name: displayName, ...stats, total, answered, responseRate, correctRate, correctRateByAnswered, score };
    });

    const sorted = [...entries].sort((a, b) => {
      if (setSortOrder === 'number') {
        if (a.number && b.number) {
          const aNum = parseInt((a.number || '').toString(), 10);
          const bNum = parseInt((b.number || '').toString(), 10);
          if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) return aNum - bNum;
          return (a.number || '').toString().localeCompare((b.number || '').toString(), 'ko');
        }
        if (a.number) return -1;
        if (b.number) return 1;
        return a.name.localeCompare(b.name, 'ko');
      }
      if (setSortOrder === 'correct') {
        if (b.correctRate !== a.correctRate) return b.correctRate - a.correctRate;
        return b.responseRate - a.responseRate;
      }
      if (setSortOrder === 'score') {
        if (b.score !== a.score) return b.score - a.score;
        return b.responseRate - a.responseRate;
      }
      // response
      if (b.responseRate !== a.responseRate) return b.responseRate - a.responseRate;
      return b.correctRate - a.correctRate;
    });

    return sorted;
  }, [isSetPresentation, setProgress, classMembersMap, isNamePublic, setSortOrder]);

  const flattenedResponses = useMemo<FlattenedResponse[]>(() => {
    const entries: FlattenedResponse[] = [];
    Object.entries(responses || {}).forEach(([studentId, resp]) => {
      if (!resp) {
        return;
      }
      if (Array.isArray(resp)) {
        resp.forEach((item, index) => {
          entries.push({ studentId, response: item, index });
        });
      } else {
        entries.push({ studentId, response: resp, index: 0 });
      }
    });

    let sorted = entries.sort((a, b) => {
      if (sortOrder === 'number') {
        const aInfo = getNumberInfo(a.studentId, a.response);
        const bInfo = getNumberInfo(b.studentId, b.response);
        
        // 게스트는 항상 뒤로
        if (aInfo.isGuest && !bInfo.isGuest) return 1;
        if (!aInfo.isGuest && bInfo.isGuest) return -1;
        
        // 둘 다 게스트면 이름 순
        if (aInfo.isGuest && bInfo.isGuest) {
          const aName = getDisplayName(a.studentId, a.response, 0);
          const bName = getDisplayName(b.studentId, b.response, 0);
          return aName.localeCompare(bName, 'ko');
        }
        
        // 둘 다 번호가 있으면 번호 순
        if (aInfo.label && bInfo.label) {
          const aNum = parseInt(aInfo.label, 10);
          const bNum = parseInt(bInfo.label, 10);
          if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) {
            return aNum - bNum;
          }
          return aInfo.label.localeCompare(bInfo.label, 'ko');
        }
        
        // 번호가 없는 경우 이름 순
        const aName = getDisplayName(a.studentId, a.response, 0);
        const bName = getDisplayName(b.studentId, b.response, 0);
        return aName.localeCompare(bName, 'ko');
      } else if (sortOrder === 'response') {
        // 응답순: 최신 응답이 먼저
        const aTime = a.response.submittedAt?.toMillis?.() ?? 0;
        const bTime = b.response.submittedAt?.toMillis?.() ?? 0;
        return bTime - aTime;
      } else if (sortOrder === 'seat') {
        // 자리배치: 정렬하지 않음 (자리배치 모드에서 별도 처리)
        return 0;
      } else {
        // 무작위: 비교 함수는 0을 반환 (정렬 후 셔플)
        return 0;
      }
    });

    // 무작위 정렬인 경우 Fisher-Yates 셔플 알고리즘 적용
    if (sortOrder === 'random') {
      const shuffled = [...sorted];
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      return shuffled;
    }

    return sorted;
  }, [responses, sortOrder, getDisplayName, getNumberInfo]);

  const formatAnswer = useCallback((response: StudentQuizResponse): string => {
    if (!isResponsePublic) {
      return '응답이 비공개로 설정되었습니다.';
    }

    if (!response?.answer) {
      return '응답 없음';
    }

    if (quiz?.type === 'choice' && Array.isArray(response.answer)) {
      if (!quiz.choices || quiz.choices.length === 0) {
        return response.answer.map((value) => `${value + 1}번`).join(', ');
      }
      return response.answer
        .map((choiceIndex) => quiz.choices?.[choiceIndex]?.text || `${choiceIndex + 1}번`)
        .join(', ');
    }

    if (Array.isArray(response.answer)) {
      return response.answer.join(', ');
    }

    return String(response.answer);
  }, [isResponsePublic, quiz]);

  const evaluateCorrectness = useCallback((response: StudentQuizResponse): boolean | null => {
    if (!quiz) {
      return response.isCorrect ?? null;
    }

    if (response.isCorrect !== undefined) {
      return response.isCorrect;
    }

    if (quiz.type === 'choice') {
      if (!Array.isArray(response.answer) || !Array.isArray(quiz.correctAnswers)) {
        return null;
      }
      const sortedAnswer = [...response.answer].sort();
      const sortedCorrect = [...quiz.correctAnswers].sort();
      return (
        sortedAnswer.length === sortedCorrect.length &&
        sortedAnswer.every((value, index) => value === sortedCorrect[index])
      );
    }

    if (quiz.type === 'short') {
      const studentAnswer =
        typeof response.answer === 'string' ? response.answer.trim().toLowerCase() : '';
      const acceptableAnswers = (quiz.answers || []).map((ans) =>
        (ans || '').trim().toLowerCase()
      );
      if (!studentAnswer || acceptableAnswers.length === 0) {
        return null;
      }
      return acceptableAnswers.includes(studentAnswer);
    }

    return null;
  }, [quiz]);

  const accuracyStats = useMemo(() => {
    const correct: FlattenedResponse[] = [];
    const incorrect: FlattenedResponse[] = [];
    flattenedResponses.forEach((entry) => {
      const result = evaluateCorrectness(entry.response);
      if (result === true) {
        correct.push(entry);
      } else if (result === false) {
        incorrect.push(entry);
      }
    });
    return { correct, incorrect };
  }, [flattenedResponses, evaluateCorrectness]);

  const formatCorrectAnswer = useCallback((): string => {
    if (!quiz) return '';
    
    if (quiz.type === 'choice' && Array.isArray(quiz.correctAnswers)) {
      if (!quiz.choices || quiz.choices.length === 0) {
        return quiz.correctAnswers.map((value) => `${value + 1}번`).join(', ');
      }
      return quiz.correctAnswers
        .map((choiceIndex) => quiz.choices?.[choiceIndex]?.text || `${choiceIndex + 1}번`)
        .join(', ');
    }
    
    if (quiz.type === 'short' && Array.isArray(quiz.answers)) {
      return quiz.answers.join(', ');
    }
    
    return '';
  }, [quiz]);

  const isCorrectAnswer = useCallback((label: string): boolean => {
    if (!quiz || !isAnswerPublic) return false;
    
    const correctAnswerText = formatCorrectAnswer();
    if (!correctAnswerText) return false;
    
    // 정답이 여러 개일 수 있으므로 쉼표로 분리하여 비교
    const correctAnswers = correctAnswerText.split(',').map(ans => ans.trim());
    return correctAnswers.includes(label.trim());
  }, [quiz, isAnswerPublic, formatCorrectAnswer]);

  const incorrectAnswers = useMemo(() => {
    const answerMap = new Map<string, number>();
    
    accuracyStats.incorrect.forEach((entry) => {
      const formatted = formatAnswer(entry.response);
      if (formatted && formatted !== '응답이 비공개로 설정되었습니다.' && formatted !== '응답 없음') {
        const count = answerMap.get(formatted) || 0;
        answerMap.set(formatted, count + 1);
      }
    });
    
    const answers = Array.from(answerMap.entries())
      .map(([answer, count]) => ({ answer, count }));
    
    if (accuracySortOrder === 'alphabetical') {
      return answers.sort((a, b) => {
        // 숫자 우선 정렬
        const aNum = parseInt(a.answer, 10);
        const bNum = parseInt(b.answer, 10);
        const aIsNum = !Number.isNaN(aNum);
        const bIsNum = !Number.isNaN(bNum);
        
        if (aIsNum && bIsNum) {
          return aNum - bNum;
        }
        if (aIsNum) return -1;
        if (bIsNum) return 1;
        
        // 한글/영문 문자순 정렬
        return a.answer.localeCompare(b.answer, 'ko');
      });
    } else {
      // 응답순: 최신 응답이 먼저 (응답 시간 기준)
      return answers.sort((a, b) => {
        // 각 답변의 최신 응답 시간 찾기
        const aEntries = accuracyStats.incorrect.filter(entry => formatAnswer(entry.response) === a.answer);
        const bEntries = accuracyStats.incorrect.filter(entry => formatAnswer(entry.response) === b.answer);
        
        const aLatestTime = Math.max(...aEntries.map(e => e.response.submittedAt?.toMillis?.() ?? 0));
        const bLatestTime = Math.max(...bEntries.map(e => e.response.submittedAt?.toMillis?.() ?? 0));
        
        return bLatestTime - aLatestTime;
      });
    }
  }, [accuracyStats.incorrect, accuracySortOrder, formatAnswer]);

  const breakdownStats = useMemo(() => {
    const counts: { label: string; count: number; responders: FlattenedResponse[] }[] = [];
    const map = new Map<string, { label: string; count: number; responders: FlattenedResponse[] }>();

    flattenedResponses.forEach((entry) => {
      if (quiz?.type === 'choice' && Array.isArray(entry.response.answer)) {
        entry.response.answer.forEach((choiceIndex) => {
          const label =
            quiz.choices?.[choiceIndex]?.text || `${choiceIndex + 1}번`;
          const key = `choice-${choiceIndex}`;
          const bucket = map.get(key) || { label, count: 0, responders: [] };
          bucket.count += 1;
          bucket.responders.push(entry);
          map.set(key, bucket);
        });
      } else {
        const answerText =
          typeof entry.response.answer === 'string'
            ? entry.response.answer.trim()
            : Array.isArray(entry.response.answer)
            ? entry.response.answer.join(', ')
            : '';
        if (answerText) {
          const normalized = answerText.toLowerCase();
          const bucket = map.get(normalized) || { label: answerText, count: 0, responders: [] };
          bucket.count += 1;
          bucket.responders.push(entry);
          map.set(normalized, bucket);
        }
      }
    });

    map.forEach((value) => counts.push(value));
    
    if (breakdownSortOrder === 'correctness') {
      // 정답순: 정답이 먼저, 오답이 나중에
      return counts.sort((a, b) => {
        const aIsCorrect = isCorrectAnswer(a.label);
        const bIsCorrect = isCorrectAnswer(b.label);
        
        if (aIsCorrect && !bIsCorrect) return -1;
        if (!aIsCorrect && bIsCorrect) return 1;
        
        // 둘 다 정답이거나 둘 다 오답이면 count 순
        return b.count - a.count;
      });
    } else if (breakdownSortOrder === 'alphabetical') {
      // 문자순: 숫자 우선, 그 다음 한글/영문
      return counts.sort((a, b) => {
        // 숫자 우선 정렬
        const aNum = parseInt(a.label, 10);
        const bNum = parseInt(b.label, 10);
        const aIsNum = !Number.isNaN(aNum);
        const bIsNum = !Number.isNaN(bNum);
        
        if (aIsNum && bIsNum) {
          return aNum - bNum;
        }
        if (aIsNum) return -1;
        if (bIsNum) return 1;
        
        // 한글/영문 문자순 정렬
        return a.label.localeCompare(b.label, 'ko');
      });
    } else if (breakdownSortOrder === 'count') {
      // 인원순: 인원이 많은 순, 같은 인원일 때는 문자순
      return counts.sort((a, b) => {
        if (a.count !== b.count) {
          return b.count - a.count;
        }
        
        // 같은 인원일 때 문자순 정렬
        const aNum = parseInt(a.label, 10);
        const bNum = parseInt(b.label, 10);
        const aIsNum = !Number.isNaN(aNum);
        const bIsNum = !Number.isNaN(bNum);
        
        if (aIsNum && bIsNum) {
          return aNum - bNum;
        }
        if (aIsNum) return -1;
        if (bIsNum) return 1;
        
        return a.label.localeCompare(b.label, 'ko');
      });
    } else {
      // 응답순: 최신 응답이 먼저
      return counts.sort((a, b) => {
        const aLatestTime = Math.max(...a.responders.map(r => r.response.submittedAt?.toMillis?.() ?? 0));
        const bLatestTime = Math.max(...b.responders.map(r => r.response.submittedAt?.toMillis?.() ?? 0));
        return bLatestTime - aLatestTime;
      });
    }
  }, [flattenedResponses, quiz?.choices, quiz?.type, breakdownSortOrder, isCorrectAnswer]);

  const summarizeResponderNames = (entries: FlattenedResponse[]) => {
    const map = new Map<
      string,
      { name: string; numberLabel: string | null; isGuest: boolean; count: number }
    >();

    entries.forEach((entry, idx) => {
      const name = getDisplayName(entry.studentId, entry.response, idx);
      const { label: numberLabel, isGuest } = getNumberInfo(entry.studentId, entry.response);
      const key = `${numberLabel ?? ''}|${name}|${isGuest ? '1' : '0'}`;
      const existing = map.get(key);
      if (existing) {
        existing.count += 1;
      } else {
        map.set(key, { name, numberLabel, isGuest, count: 1 });
      }
    });

    return Array.from(map.values())
      .sort((a, b) => {
        if (a.isGuest && !b.isGuest) return 1;
        if (!a.isGuest && b.isGuest) return -1;

        const hasNumberA = !!(a.numberLabel && a.numberLabel !== 'G');
        const hasNumberB = !!(b.numberLabel && b.numberLabel !== 'G');

        if (hasNumberA && hasNumberB) {
          const numA = parseInt(a.numberLabel as string, 10);
          const numB = parseInt(b.numberLabel as string, 10);
          if (!Number.isNaN(numA) && !Number.isNaN(numB) && numA !== numB) {
            return numA - numB;
          }
        } else if (hasNumberA) {
          return -1;
        } else if (hasNumberB) {
          return 1;
        }

        return a.name.localeCompare(b.name, 'ko');
      })
      .map((item, index) => {
        const numberPrefix = item.numberLabel && String(item.numberLabel).trim() !== '';
        const nameAlreadyHasNumber = numberPrefix && item.name.startsWith(`${item.numberLabel}. `);
        const displayName = item.isGuest
          ? `G ${item.name}`.trim()
          : nameAlreadyHasNumber
            ? item.name
            : `${item.numberLabel || ''}${item.numberLabel ? ' ' : ''}${item.name}`.trim();
        return {
          key: `${item.numberLabel ?? 'none'}-${item.name}-${item.isGuest}-${index}`,
          displayName,
          count: item.count,
        };
      });
  };

  if (error) {
    return (
      <PageContainer>
        <CenteredMessage>{error}</CenteredMessage>
      </PageContainer>
    );
  }

  if (!classCode || (!quizId && !isSetPresentation)) {
    return (
      <PageContainer>
        <CenteredMessage>필수 정보가 전달되지 않았습니다.</CenteredMessage>
      </PageContainer>
    );
  }

  if (isSetPresentation) {
    return (
      <PageContainer>
        <PresentationHeader>
          <PresentationHeaderContent>
            <PageTitle>문제 세트 프레젠테이션</PageTitle>
            <QuestionTitle>{setTitle || '문제 세트'}</QuestionTitle>
            <OptionsSummary>
              <OptionToggleBlock>
                <OptionToggleLabel>이름</OptionToggleLabel>
                <OptionToggleButtons $isActive={isNamePublic} onClick={() => setIsNamePublic((prev) => !prev)}>
                  <OptionToggleButton $isActive={!isNamePublic}>숨김</OptionToggleButton>
                  <OptionToggleButton $isActive={isNamePublic}>표시</OptionToggleButton>
                </OptionToggleButtons>
              </OptionToggleBlock>
              <OptionToggleBlock>
                <OptionToggleLabel>정답률</OptionToggleLabel>
                <OptionToggleButtons $isActive={isCorrectRateVisible} onClick={() => setIsCorrectRateVisible((prev) => !prev)}>
                  <OptionToggleButton $isActive={!isCorrectRateVisible}>숨김</OptionToggleButton>
                  <OptionToggleButton $isActive={isCorrectRateVisible}>표시</OptionToggleButton>
                </OptionToggleButtons>
              </OptionToggleBlock>
              <OptionToggleBlock>
                <OptionToggleLabel>점수</OptionToggleLabel>
                <OptionToggleButtons $isActive={isScoreVisible} onClick={() => setIsScoreVisible((prev) => !prev)}>
                  <OptionToggleButton $isActive={!isScoreVisible}>숨김</OptionToggleButton>
                  <OptionToggleButton $isActive={isScoreVisible}>표시</OptionToggleButton>
                </OptionToggleButtons>
              </OptionToggleBlock>
            </OptionsSummary>
          </PresentationHeaderContent>
          <PresentationHeaderRight>
            <PresentationCloseButton onClick={handleCloseWindow} title="창 닫기">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </PresentationCloseButton>
          </PresentationHeaderRight>
        </PresentationHeader>

        <ViewToggleRow>
          <SortToggleRow>
            <SortToggleButtons>
              <SortToggleButton
                type="button"
                $active={setSortOrder === 'number'}
                $disabled={false}
                onClick={() => setSetSortOrder('number')}
              >
                번호순
              </SortToggleButton>
              <SortToggleButton
                type="button"
                $active={setSortOrder === 'response'}
                $disabled={false}
                onClick={() => setSetSortOrder('response')}
              >
                응답률순
              </SortToggleButton>
              <SortToggleButton
                type="button"
                $active={setSortOrder === 'correct'}
                $disabled={false}
                onClick={() => setSetSortOrder('correct')}
              >
                정답률순
              </SortToggleButton>
              <SortToggleButton
                type="button"
                $active={setSortOrder === 'score'}
                $disabled={false}
                onClick={() => setSetSortOrder('score')}
              >
                점수순
              </SortToggleButton>
              <SortToggleButton
                type="button"
                $active={setSortOrder === 'seat'}
                $disabled={false}
                onClick={() => setSetSortOrder('seat')}
              >
                자리순
              </SortToggleButton>
            </SortToggleButtons>
          </SortToggleRow>
          <SizeControlButtons>
            <SizeControlButton
              onClick={(e) => {
                e.stopPropagation();
                setBlockSize(prev => {
                  const newSize = Math.max(0.7, prev - 0.1);
                  try {
                    localStorage.setItem('presentation_blockSize', newSize.toString());
                  } catch {
                    // localStorage 저장 실패 시 무시
                  }
                  return newSize;
                });
              }}
              disabled={blockSize <= 0.7}
              aria-label="블럭 크기 줄이기"
            >
              <svg width="14" height="14" viewBox="0 0 12 12" fill="none">
                <path d="M2 6h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </SizeControlButton>
            <SizeControlButton
              onClick={(e) => {
                e.stopPropagation();
                setBlockSize(prev => {
                  const newSize = Math.min(2.5, prev + 0.1);
                  try {
                    localStorage.setItem('presentation_blockSize', newSize.toString());
                  } catch {
                    // localStorage 저장 실패 시 무시
                  }
                  return newSize;
                });
              }}
              disabled={blockSize >= 2.5}
              aria-label="블럭 크기 늘리기"
            >
              <svg width="14" height="14" viewBox="0 0 12 12" fill="none">
                <path d="M6 2v8M2 6h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </SizeControlButton>
          </SizeControlButtons>
        </ViewToggleRow>

        {setSortOrder === 'seat' ? (
          // 자리배치 뷰
          seatArrangementError ? (
            <CenteredMessage>{seatArrangementError}</CenteredMessage>
          ) : !seatArrangementData || seatArrangementData.length === 0 ? (
            <CenteredMessage>자리배치를 먼저 해주세요</CenteredMessage>
          ) : (
            (() => {
              const progressByStudentId = Object.fromEntries(
                setProgressList.map((entry) => [entry.studentId, entry])
              );
              const usedRows = new Set<number>();
              const usedCols = new Set<number>();
              seatArrangementData.forEach((s: any) => {
                usedRows.add(s.seatRow);
                usedRows.add(s.seatRow + 1);
                usedCols.add(s.seatCol);
                usedCols.add(s.seatCol + 1);
              });
              const rowsArray = Array.from({ length: 16 }, (_, i) =>
                usedRows.has(i) ? '0.7fr' : '0.35fr'
              );
              const colsArray = Array.from({ length: 16 }, (_, i) =>
                usedCols.has(i) ? '1fr' : '0.5fr'
              );
              const gridTemplateRows = isSeatRotated ? [...rowsArray].reverse().join(' ') : rowsArray.join(' ');
              const gridTemplateColumns = isSeatRotated ? [...colsArray].reverse().join(' ') : colsArray.join(' ');
              const seatStudentIds = new Set(seatArrangementData.map((s: any) => s.studentId));
              const guestEntries = setProgressList.filter((entry) => !seatStudentIds.has(entry.studentId));
              return (
                <SeatModeContainer>
                  <SeatModeScrollArea>
                    <SeatModeGridWrapper>
                      <SeatGridContent
                        $blockSize={blockSize}
                        style={{ gridTemplateColumns, gridTemplateRows }}
                      >
                        {seatArrangementData.map((seatStudent: any) => {
                          const entry = progressByStudentId[seatStudent.studentId];
                          const hasProgress = !!entry;
                          const rotatedRow = isSeatRotated ? 14 - seatStudent.seatRow : seatStudent.seatRow;
                          const rotatedCol = isSeatRotated ? 14 - seatStudent.seatCol : seatStudent.seatCol;
                          const displayName = hasProgress ? entry.name : (seatStudent.number ? `${seatStudent.number}. ${seatStudent.name}` : seatStudent.name);
                          return (
                            <ResponseCard
                              key={seatStudent.studentId}
                              $blockSize={blockSize}
                              style={{
                                gridRow: `${rotatedRow + 1} / span 2`,
                                gridColumn: `${rotatedCol + 1} / span 2`,
                                opacity: hasProgress ? 1 : 0.55,
                                filter: hasProgress ? 'none' : 'grayscale(0.9)',
                                border: hasProgress ? '2px solid var(--color-accent-strong)' : 'none',
                                padding: `${12 * blockSize}px ${18 * blockSize}px`,
                                minHeight: `${100 * blockSize}px`,
                              }}
                            >
                              <ResponseHeader style={{ justifyContent: 'flex-end', textAlign: 'right' }}>
                                <ResponderName $blockSize={blockSize}>{displayName}</ResponderName>
                              </ResponseHeader>
                              <ResponseBody style={{ alignItems: 'flex-end', gap: 10 }}>
                                <div style={{ width: '100%', margin: '4px 0' }}>
                                  {isScoreVisible && hasProgress && (
                                    <div style={{ textAlign: 'center', fontSize: 28, fontWeight: 800, color: 'var(--color-text-primary)', marginBottom: '8px', textShadow: '0 1px 2px rgba(0,0,0,0.2)' }}>
                                      {entry.score}점
                                    </div>
                                  )}
                                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontWeight: 700, fontSize: 14, color: 'var(--color-text-primary)', marginBottom: '4px', gap: 8 }}>
                                    {isCorrectRateVisible && hasProgress ? (
                                      <span style={{ padding: '2px 8px', borderRadius: 6, background: 'var(--color-surface)', border: '1px solid var(--color-border)' }}>
                                        정답률 {Math.round((entry.correctRateByAnswered ?? 0) * 100)}%
                                      </span>
                                    ) : (
                                      <span />
                                    )}
                                    {hasProgress && (
                                      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <span>
                                          <span style={{ color: 'var(--presentation-rate-bar-color)' }}>{entry.correct || 0}</span>
                                          <span style={{ color: 'var(--presentation-rate-bar-color-light)' }}>+{entry.essay || 0}</span>
                                          <span style={{ color: 'var(--color-text-primary)' }}> / {entry.total || 0}</span>
                                        </span>
                                        <span style={{ padding: '2px 8px', borderRadius: 6, background: 'var(--color-surface)', border: '1px solid var(--color-border)' }}>
                                          {Math.round(entry.responseRate * 100)}%
                                        </span>
                                      </span>
                                    )}
                                  </div>
                                  {hasProgress && (
                                    <div style={{ width: '100%', height: 10, borderRadius: 999, background: 'var(--color-border)', overflow: 'hidden' }}>
                                      <div
                                        style={{
                                          width: `${Math.min(100, entry.responseRate * 100)}%`,
                                          height: '100%',
                                          position: 'relative',
                                          background: 'var(--presentation-response-rate-bar)',
                                          transition: 'width 0.2s ease',
                                        }}
                                      >
                                        <div
                                          style={{
                                            position: 'absolute',
                                            right: 0,
                                            top: 0,
                                            bottom: 0,
                                            width: 2,
                                            background: 'var(--presentation-response-rate-bar-edge)',
                                            zIndex: 1,
                                          }}
                                        />
                                        {isCorrectRateVisible && (entry.answered || 0) > 0 && (
                                          <>
                                            <div
                                              style={{
                                                position: 'absolute',
                                                left: 0,
                                                top: 0,
                                                bottom: 0,
                                                width: `${Math.min(100, ((entry.correct || 0) / (entry.answered || 1)) * 100)}%`,
                                                background: 'var(--presentation-rate-bar-color)',
                                                transition: 'width 0.2s ease',
                                              }}
                                            />
                                            <div
                                              style={{
                                                position: 'absolute',
                                                left: `${Math.min(100, ((entry.correct || 0) / (entry.answered || 1)) * 100)}%`,
                                                top: 0,
                                                bottom: 0,
                                                width: `${Math.min(100, ((entry.essay || 0) / (entry.answered || 1)) * 100)}%`,
                                                background: 'var(--presentation-rate-bar-color-light)',
                                                transition: 'width 0.2s ease',
                                              }}
                                            />
                                          </>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </ResponseBody>
                            </ResponseCard>
                          );
                        })}
                      </SeatGridContent>
                    </SeatModeGridWrapper>
                  </SeatModeScrollArea>
                  <RotateButtonContainer>
                    <RotateButton onClick={() => setIsSeatRotated(!isSeatRotated)} title="180도 회전">
                      <RotateIconWrapper>
                        <RotateArrowIcon>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M12 4v16M12 4l-4 4M12 4l4 4M12 20l-4-4M12 20l4-4" />
                          </svg>
                        </RotateArrowIcon>
                        <RotateNumberIcon>180</RotateNumberIcon>
                      </RotateIconWrapper>
                    </RotateButton>
                  </RotateButtonContainer>
                  {guestEntries.length > 0 && (
                    <GuestSection>
                      <GuestSectionTitle>게스트</GuestSectionTitle>
                      <ResponsesGrid $blockSize={blockSize}>
                        {guestEntries.map((entry) => (
                          <ResponseCard key={entry.studentId} $blockSize={blockSize} style={{ width: '100%' }}>
                            <ResponseHeader style={{ justifyContent: 'flex-end', textAlign: 'right' }}>
                              <ResponderName $blockSize={blockSize}>{entry.name}</ResponderName>
                            </ResponseHeader>
                            <ResponseBody style={{ alignItems: 'flex-end', gap: 10 }}>
                              <div style={{ width: '100%', margin: '4px 0' }}>
                                {isScoreVisible && (
                                  <div style={{ textAlign: 'center', fontSize: 28, fontWeight: 800, color: 'var(--color-text-primary)', marginBottom: '8px' }}>
                                    {entry.score}점
                                  </div>
                                )}
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontWeight: 700, fontSize: 14, color: 'var(--color-text-primary)', marginBottom: '4px' }}>
                                  {isCorrectRateVisible && (
                                    <span style={{ padding: '2px 8px', borderRadius: 6, background: 'var(--color-surface)', border: '1px solid var(--color-border)' }}>
                                      정답률 {Math.round((entry.correctRateByAnswered ?? 0) * 100)}%
                                    </span>
                                  )}
                                  <span style={{ padding: '2px 8px', borderRadius: 6, background: 'var(--color-surface)', border: '1px solid var(--color-border)' }}>
                                    {Math.round(entry.responseRate * 100)}%
                                  </span>
                                </div>
                                <div style={{ width: '100%', height: 10, borderRadius: 999, background: 'var(--color-border)', overflow: 'hidden' }}>
                                  <div
                                    style={{
                                      width: `${Math.min(100, entry.responseRate * 100)}%`,
                                      height: '100%',
                                      position: 'relative',
                                      background: 'var(--presentation-response-rate-bar)',
                                      transition: 'width 0.2s ease',
                                    }}
                                  >
                                    <div
                                      style={{
                                        position: 'absolute',
                                        right: 0,
                                        top: 0,
                                        bottom: 0,
                                        width: 2,
                                        background: 'var(--presentation-response-rate-bar-edge)',
                                        zIndex: 1,
                                      }}
                                    />
                                    {isCorrectRateVisible && (entry.answered || 0) > 0 && (
                                      <>
                                        <div
                                          style={{
                                            position: 'absolute',
                                            left: 0,
                                            top: 0,
                                            bottom: 0,
                                            width: `${Math.min(100, ((entry.correct || 0) / (entry.answered || 1)) * 100)}%`,
                                            background: 'var(--presentation-rate-bar-color)',
                                            transition: 'width 0.2s ease',
                                          }}
                                        />
                                        <div
                                          style={{
                                            position: 'absolute',
                                            left: `${Math.min(100, ((entry.correct || 0) / (entry.answered || 1)) * 100)}%`,
                                            top: 0,
                                            bottom: 0,
                                            width: `${Math.min(100, ((entry.essay || 0) / (entry.answered || 1)) * 100)}%`,
                                            background: 'var(--presentation-rate-bar-color-light)',
                                            transition: 'width 0.2s ease',
                                          }}
                                        />
                                      </>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </ResponseBody>
                          </ResponseCard>
                        ))}
                      </ResponsesGrid>
                    </GuestSection>
                  )}
                </SeatModeContainer>
              );
            })()
          )
        ) : isSetLoading ? (
          <CenteredMessage>세트 진행도를 불러오는 중입니다...</CenteredMessage>
        ) : setProgressList.length === 0 ? (
          <CenteredMessage>표시할 진행 정보가 없습니다.</CenteredMessage>
        ) : (
          <ResponsesGrid $blockSize={blockSize} style={{ justifyItems: 'end' }}>
            {setProgressList.map((entry) => (
              <ResponseCard key={entry.studentId} $blockSize={blockSize} style={{ width: '100%' }}>
                <ResponseHeader style={{ justifyContent: 'flex-end', textAlign: 'right' }}>
                  <ResponderName $blockSize={blockSize}>{entry.name}</ResponderName>
                </ResponseHeader>
                <ResponseBody style={{ alignItems: 'flex-end', gap: 10 }}>
                  <div style={{ width: '100%', margin: '4px 0' }}>
                    {isScoreVisible && (
                      <div
                        style={{
                          textAlign: 'center',
                          fontSize: 28,
                          fontWeight: 800,
                          color: 'var(--color-text-primary)',
                          marginBottom: '8px',
                          textShadow: '0 1px 2px rgba(0,0,0,0.2)',
                        }}
                      >
                        {entry.score}점
                      </div>
                    )}
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        fontWeight: 700,
                        fontSize: 14,
                        color: 'var(--color-text-primary)',
                        marginBottom: '4px',
                        gap: 8,
                      }}
                    >
                      {isCorrectRateVisible ? (
                        <span
                          style={{
                            padding: '2px 8px',
                            borderRadius: 6,
                            background: 'var(--color-surface)',
                            border: '1px solid var(--color-border)',
                          }}
                        >
                          정답률 {Math.round((entry.correctRateByAnswered ?? 0) * 100)}%
                        </span>
                      ) : (
                        <span />
                      )}
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span>
                          <span style={{ color: 'var(--presentation-rate-bar-color)' }}>{entry.correct || 0}</span>
                          <span style={{ color: 'var(--presentation-rate-bar-color-light)' }}>+{entry.essay || 0}</span>
                          <span style={{ color: 'var(--color-text-primary)' }}> / {entry.total || 0}</span>
                        </span>
                        <span
                          style={{
                            padding: '2px 8px',
                            borderRadius: 6,
                            background: 'var(--color-surface)',
                            border: '1px solid var(--color-border)',
                          }}
                        >
                          {Math.round(entry.responseRate * 100)}%
                        </span>
                      </span>
                    </div>
                    <div
                      style={{
                        width: '100%',
                        height: 10,
                        borderRadius: 999,
                        background: 'var(--color-border)',
                        overflow: 'hidden',
                      }}
                    >
                      {/* 응답률 막대: 배경보다 어둡/밝은 회색, 항상 표시 */}
                      <div
                        style={{
                          width: `${Math.min(100, entry.responseRate * 100)}%`,
                          height: '100%',
                          position: 'relative',
                          background: 'var(--presentation-response-rate-bar)',
                          transition: 'width 0.2s ease',
                        }}
                      >
                        {/* 오른쪽 끝 세로라인 */}
                        <div
                          style={{
                            position: 'absolute',
                            right: 0,
                            top: 0,
                            bottom: 0,
                            width: 2,
                            background: 'var(--presentation-response-rate-bar-edge)',
                            zIndex: 1,
                          }}
                        />
                        {isCorrectRateVisible && (entry.answered || 0) > 0 && (
                          <>
                            {/* 정답 구간: 라이트=메인테마 초록, 다크=흰색 */}
                            <div
                              style={{
                                position: 'absolute',
                                left: 0,
                                top: 0,
                                bottom: 0,
                                width: `${Math.min(100, ((entry.correct || 0) / (entry.answered || 1)) * 100)}%`,
                                background: 'var(--presentation-rate-bar-color)',
                                transition: 'width 0.2s ease',
                              }}
                            />
                            {/* 서술형 정답 구간: 60% 밝기 */}
                            <div
                              style={{
                                position: 'absolute',
                                left: `${Math.min(100, ((entry.correct || 0) / (entry.answered || 1)) * 100)}%`,
                                top: 0,
                                bottom: 0,
                                width: `${Math.min(100, ((entry.essay || 0) / (entry.answered || 1)) * 100)}%`,
                                background: 'var(--presentation-rate-bar-color-light)',
                                transition: 'width 0.2s ease',
                              }}
                            />
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </ResponseBody>
              </ResponseCard>
            ))}
          </ResponsesGrid>
        )}
        <ThemeToggleWrapper>
          <ThemeToggleButton
            $isDark={themeMode === 'dark'}
            onClick={handleThemeToggle}
            aria-label="테마 전환"
          >
            <ThemeToggleOption $isActive={themeMode === 'light'} $isDark={themeMode === 'dark'}>
              일반
            </ThemeToggleOption>
            <ThemeToggleOption $isActive={themeMode === 'dark'} $isDark={themeMode === 'dark'}>
              다크
            </ThemeToggleOption>
          </ThemeToggleButton>
        </ThemeToggleWrapper>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PresentationHeader>
        <PresentationHeaderContent>
          <PageTitle>프레젠테이션</PageTitle>
          <QuestionTitle 
            onClick={() => quiz && setShowQuizDetailModal(true)}
            $isClickable={!!quiz}
          >
            {quiz?.question || '문제를 불러오는 중입니다...'}
          </QuestionTitle>
          {quiz?.questionImageUrl && quiz.questionImageUrl.trim() && (
            <PresentationQuestionImageBoxSmall>
              <PresentationQuestionImageSmall src={quiz.questionImageUrl} alt="질문 이미지" />
            </PresentationQuestionImageBoxSmall>
          )}
          {quiz?.guide && <QuestionGuide>{quiz.guide}</QuestionGuide>}
          <OptionsSummary>
            <OptionToggleBlock>
              <OptionToggleLabel>응답</OptionToggleLabel>
              <OptionToggleButtons
                $isActive={isResponsePublic}
                $isDisabled={viewMode === 'breakdown'}
                onClick={() => viewMode !== 'breakdown' && setIsResponsePublic(!isResponsePublic)}
              >
                <OptionToggleButton $isActive={!isResponsePublic} $isDisabled={viewMode === 'breakdown'}>숨김</OptionToggleButton>
                <OptionToggleButton $isActive={isResponsePublic} $isDisabled={viewMode === 'breakdown'}>표시</OptionToggleButton>
              </OptionToggleButtons>
            </OptionToggleBlock>
            <OptionToggleBlock>
              <OptionToggleLabel>이름</OptionToggleLabel>
              <OptionToggleButtons $isActive={isNamePublic} onClick={() => setIsNamePublic(!isNamePublic)}>
                <OptionToggleButton $isActive={!isNamePublic}>숨김</OptionToggleButton>
                <OptionToggleButton $isActive={isNamePublic}>표시</OptionToggleButton>
              </OptionToggleButtons>
            </OptionToggleBlock>
            <OptionToggleBlock>
              <OptionToggleLabel>정답</OptionToggleLabel>
              <OptionToggleButtons
                $isActive={isAnswerPublic}
                $isDisabled={viewMode === 'accuracy'}
                onClick={() => viewMode !== 'accuracy' && setIsAnswerPublic(!isAnswerPublic)}
              >
                <OptionToggleButton $isActive={!isAnswerPublic} $isDisabled={viewMode === 'accuracy'}>숨김</OptionToggleButton>
                <OptionToggleButton $isActive={isAnswerPublic} $isDisabled={viewMode === 'accuracy'}>표시</OptionToggleButton>
              </OptionToggleButtons>
            </OptionToggleBlock>
          </OptionsSummary>
        </PresentationHeaderContent>
        <PresentationHeaderRight>
          <PresentationCloseButton onClick={handleCloseWindow} title="창 닫기">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </PresentationCloseButton>
        </PresentationHeaderRight>
      </PresentationHeader>

      <ViewToggleRow>
        <ViewToggleButtonsContainer>
          <ViewToggleButton
            type="button"
            $active={viewMode === 'students'}
            onClick={() => handleViewModeChange('students')}
          >
            <ViewToggleLabelFull>멤버별 응답 결과</ViewToggleLabelFull>
            <ViewToggleLabelShort>응답</ViewToggleLabelShort>
          </ViewToggleButton>
          <ViewToggleButton
            type="button"
            $active={viewMode === 'accuracy'}
            onClick={() => handleViewModeChange('accuracy')}
          >
            <ViewToggleLabelFull>정답·오답 결과</ViewToggleLabelFull>
            <ViewToggleLabelShort>정.오답</ViewToggleLabelShort>
          </ViewToggleButton>
          <ViewToggleButton
            type="button"
            $active={viewMode === 'breakdown'}
            onClick={() => handleViewModeChange('breakdown')}
          >
            <ViewToggleLabelFull>항목별 결과</ViewToggleLabelFull>
            <ViewToggleLabelShort>항목</ViewToggleLabelShort>
          </ViewToggleButton>
        </ViewToggleButtonsContainer>
        <SizeControlButtons>
          <SizeControlButton
            onClick={(e) => {
              e.stopPropagation();
              setBlockSize(prev => {
                const newSize = Math.max(0.7, prev - 0.1);
                // localStorage에 저장
                try {
                  localStorage.setItem('presentation_blockSize', newSize.toString());
                } catch {
                  // localStorage 저장 실패 시 무시
                }
                return newSize;
              });
            }}
            disabled={blockSize <= 0.7}
            aria-label="블럭 크기 줄이기"
          >
            <svg width="14" height="14" viewBox="0 0 12 12" fill="none">
              <path d="M2 6h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </SizeControlButton>
          <SizeControlButton
            onClick={(e) => {
              e.stopPropagation();
              setBlockSize(prev => {
                const newSize = Math.min(2.5, prev + 0.1);
                // localStorage에 저장
                try {
                  localStorage.setItem('presentation_blockSize', newSize.toString());
                } catch {
                  // localStorage 저장 실패 시 무시
                }
                return newSize;
              });
            }}
            disabled={blockSize >= 2.5}
            aria-label="블럭 크기 늘리기"
          >
            <svg width="14" height="14" viewBox="0 0 12 12" fill="none">
              <path d="M6 2v8M2 6h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </SizeControlButton>
        </SizeControlButtons>
      </ViewToggleRow>

      {viewMode === 'students' && (
        <SortToggleRow>
          <SortToggleButtons>
            <SortToggleButton
              type="button"
              $active={sortOrder === 'number'}
              $disabled={!isNamePublic}
              onClick={() => isNamePublic && dispatchViewState({ type: 'SET_SORT_ORDER', sortOrder: 'number' })}
            >
              번호순
            </SortToggleButton>
            <SortToggleButton
              type="button"
              $active={sortOrder === 'response'}
              $disabled={false}
              onClick={() => dispatchViewState({ type: 'SET_SORT_ORDER', sortOrder: 'response' })}
            >
              응답순
            </SortToggleButton>
            <SortToggleButton
              type="button"
              $active={sortOrder === 'random'}
              $disabled={false}
              onClick={() => dispatchViewState({ type: 'SET_SORT_ORDER', sortOrder: 'random' })}
            >
              무작위
            </SortToggleButton>
            <SortToggleButton
              type="button"
              $active={sortOrder === 'seat'}
              $disabled={false}
              onClick={() => dispatchViewState({ type: 'SET_SORT_ORDER', sortOrder: 'seat' })}
            >
              자리배치
            </SortToggleButton>
          </SortToggleButtons>
        </SortToggleRow>
      )}

      {viewMode === 'accuracy' && (
        <SortToggleRow>
          <SortToggleButtons>
            <SortToggleButton
              type="button"
              $active={accuracySortOrder === 'response'}
              $disabled={!isResponsePublic}
              onClick={() => isResponsePublic && dispatchViewState({ type: 'SET_ACCURACY_SORT_ORDER', accuracySortOrder: 'response' })}
            >
              응답순
            </SortToggleButton>
            <SortToggleButton
              type="button"
              $active={accuracySortOrder === 'alphabetical'}
              $disabled={!isResponsePublic}
              onClick={() => isResponsePublic && dispatchViewState({ type: 'SET_ACCURACY_SORT_ORDER', accuracySortOrder: 'alphabetical' })}
            >
              문자순
            </SortToggleButton>
          </SortToggleButtons>
        </SortToggleRow>
      )}

      {viewMode === 'breakdown' && (
        <SortToggleRow>
          <SortToggleButtons>
            <SortToggleButton
              type="button"
              $active={breakdownSortOrder === 'correctness'}
              $disabled={!isAnswerPublic}
              onClick={() => isAnswerPublic && dispatchViewState({ type: 'SET_BREAKDOWN_SORT_ORDER', breakdownSortOrder: 'correctness' })}
            >
              정답순
            </SortToggleButton>
            <SortToggleButton
              type="button"
              $active={breakdownSortOrder === 'response'}
              $disabled={false}
              onClick={() => dispatchViewState({ type: 'SET_BREAKDOWN_SORT_ORDER', breakdownSortOrder: 'response' })}
            >
              응답순
            </SortToggleButton>
            <SortToggleButton
              type="button"
              $active={breakdownSortOrder === 'alphabetical'}
              $disabled={false}
              onClick={() => dispatchViewState({ type: 'SET_BREAKDOWN_SORT_ORDER', breakdownSortOrder: 'alphabetical' })}
            >
              문자순
            </SortToggleButton>
            <SortToggleButton
              type="button"
              $active={breakdownSortOrder === 'count'}
              $disabled={false}
              onClick={() => dispatchViewState({ type: 'SET_BREAKDOWN_SORT_ORDER', breakdownSortOrder: 'count' })}
            >
              인원순
            </SortToggleButton>
          </SortToggleButtons>
        </SortToggleRow>
      )}

      {isLoading ? (
        <CenteredMessage>응답을 불러오는 중입니다...</CenteredMessage>
      ) : flattenedResponses.length === 0 && sortOrder !== 'seat' ? (
        <CenteredMessage>표시할 응답이 아직 없습니다.</CenteredMessage>
      ) : viewMode === 'students' && sortOrder === 'seat' ? (
        // 자리배치 모드
        (() => {
          if (seatArrangementError) {
            return (
              <SeatModeContainer>
                <CenteredMessage>{seatArrangementError}</CenteredMessage>
              </SeatModeContainer>
            );
          }

          if (!seatArrangementData || seatArrangementData.length === 0) {
            return (
              <SeatModeContainer>
                <CenteredMessage>자리배치를 먼저 해주세요</CenteredMessage>
              </SeatModeContainer>
            );
          }

          // 자리 정보가 있는 학생들과 게스트 분리
          const seatStudentIds = new Set(seatArrangementData.map(s => s.studentId));
          const guestResponses = flattenedResponses.filter(entry => !seatStudentIds.has(entry.studentId));

          // 사용된 행/열 계산
          const usedRows = new Set<number>();
          const usedCols = new Set<number>();
          
          seatArrangementData.forEach((seatStudent: any) => {
            usedRows.add(seatStudent.seatRow);
            usedRows.add(seatStudent.seatRow + 1);
            usedCols.add(seatStudent.seatCol);
            usedCols.add(seatStudent.seatCol + 1);
          });

          // 동적 그리드 템플릿 생성 (높이를 줄여서 컴팩트하게)
          const rowsArray = Array.from({ length: 16 }, (_, i) => 
            usedRows.has(i) ? '0.7fr' : '0.35fr'
          );
          const colsArray = Array.from({ length: 16 }, (_, i) => 
            usedCols.has(i) ? '1fr' : '0.5fr'
          );
          
          // 180도 회전 시 그리드 템플릿도 역순으로 배열 (원본 배열 변경 방지를 위해 복사)
          const gridTemplateRows = isSeatRotated 
            ? [...rowsArray].reverse().join(' ')
            : rowsArray.join(' ');
          
          const gridTemplateColumns = isSeatRotated
            ? [...colsArray].reverse().join(' ')
            : colsArray.join(' ');

          return (
            <SeatModeContainer>
              <SeatModeScrollArea>
                <SeatModeGridWrapper>
                  <SeatGridContent
                    $blockSize={blockSize}
                    style={{
                      gridTemplateColumns,
                      gridTemplateRows,
                    }}
                  >
                    {seatArrangementData.map((seatStudent: any) => {
                      const responseEntry = flattenedResponses.find(entry => entry.studentId === seatStudent.studentId);
                      const hasResponse = !!responseEntry;
                      const response = responseEntry?.response;
                      const numberLabel = seatStudent.number || '';

                      // 180도 회전 적용
                      // 그리드가 16x16이고 템플릿이 역순이면, 인덱스 0은 15가 되고, 인덱스 n은 15-n이 됨
                      // 블럭이 2x2를 차지하므로 (seatRow, seatCol)에서 시작하는 블럭은 회전 후 (14-seatRow, 14-seatCol)에서 시작
                      const rotatedRow = isSeatRotated 
                        ? 14 - seatStudent.seatRow
                        : seatStudent.seatRow;
                      const rotatedCol = isSeatRotated 
                        ? 14 - seatStudent.seatCol
                        : seatStudent.seatCol;

                      return (
                        <ResponseCard
                          key={seatStudent.studentId}
                          $blockSize={blockSize}
                          style={{
                            gridRow: `${rotatedRow + 1} / span 2`,
                            gridColumn: `${rotatedCol + 1} / span 2`,
                            opacity: hasResponse ? 1 : 0.55,
                            filter: hasResponse ? 'none' : 'grayscale(0.9)',
                            border: hasResponse ? '2px solid var(--color-accent-strong)' : 'none',
                            padding: `${12 * blockSize}px ${18 * blockSize}px`,
                            minHeight: `${100 * blockSize}px`,
                          }}
                        >
                          {isNamePublic && (
                            <ResponseHeader style={{ marginBottom: `${6 * blockSize}px` }}>
                              <ResponderName $blockSize={blockSize}>
                                {numberLabel ? `${numberLabel}. ${seatStudent.name}` : seatStudent.name}
                              </ResponderName>
                            </ResponseHeader>
                          )}
                          <ResponseBody style={{ gap: `${4 * blockSize}px` }}>
                            {isAnswerPublic && hasResponse && response && (() => {
                              const correctness = evaluateCorrectness(response);
                              if (correctness === null) return null;
                              return (
                                <CorrectnessBadge $isCorrect={correctness} $blockSize={blockSize}>
                                  {correctness ? (
                                    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style={{ strokeWidth: 3 }}>
                                      <circle cx="12" cy="12" r="9" stroke="currentColor" fill="none" />
                                    </svg>
                                  ) : (
                                    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style={{ strokeWidth: 3 }}>
                                      <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                                    </svg>
                                  )}
                                </CorrectnessBadge>
                              );
                            })()}
                            <ResponseText 
                              $isPlaceholder={!isResponsePublic || !hasResponse} 
                              $blockSize={blockSize}
                              style={{ minHeight: `${40 * blockSize}px` }}
                            >
                              {hasResponse && response ? formatAnswer(response) : '응답 없음'}
                            </ResponseText>
                          </ResponseBody>
                        </ResponseCard>
                      );
                    })}
                  </SeatGridContent>
                </SeatModeGridWrapper>
              </SeatModeScrollArea>
              
              <RotateButtonContainer>
                <RotateButton onClick={() => setIsSeatRotated(!isSeatRotated)} title="180도 회전">
                  <RotateIconWrapper>
                    <RotateArrowIcon>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 4v16M12 4l-4 4M12 4l4 4M12 20l-4-4M12 20l4-4" />
                      </svg>
                    </RotateArrowIcon>
                    <RotateNumberIcon>180</RotateNumberIcon>
                  </RotateIconWrapper>
                </RotateButton>
              </RotateButtonContainer>
              
              {guestResponses.length > 0 && (
                <GuestSection>
                  <GuestSectionTitle>게스트</GuestSectionTitle>
                  <ResponsesGrid $blockSize={blockSize}>
                    {guestResponses.map((entry, idx) => {
                      return (
                        <ResponseCard key={`guest-${entry.studentId}-${idx}`} $blockSize={blockSize}>
                          <ResponseHeader>
                            <ResponderName $blockSize={blockSize}>{getDisplayName(entry.studentId, entry.response, idx)}</ResponderName>
                          </ResponseHeader>
                          <ResponseBody>
                            {isAnswerPublic && (() => {
                              const correctness = evaluateCorrectness(entry.response);
                              if (correctness === null) return null;
                              return (
                                <CorrectnessBadge $isCorrect={correctness} $blockSize={blockSize}>
                                  {correctness ? (
                                    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style={{ strokeWidth: 3 }}>
                                      <circle cx="12" cy="12" r="9" stroke="currentColor" fill="none" />
                                    </svg>
                                  ) : (
                                    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style={{ strokeWidth: 3 }}>
                                      <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                                    </svg>
                                  )}
                                </CorrectnessBadge>
                              );
                            })()}
                            <ResponseText $isPlaceholder={!isResponsePublic} $blockSize={blockSize}>
                              {formatAnswer(entry.response)}
                            </ResponseText>
                          </ResponseBody>
                        </ResponseCard>
                      );
                    })}
                  </ResponsesGrid>
                </GuestSection>
              )}
            </SeatModeContainer>
          );
        })()
      ) : viewMode === 'students' ? (
        <ResponsesGrid $blockSize={blockSize}>
          {flattenedResponses.map((entry, idx) => {
            return (
              <ResponseCard key={`${entry.studentId}-${idx}`} $blockSize={blockSize}>
                <ResponseHeader>
                  <ResponderName $blockSize={blockSize}>{getDisplayName(entry.studentId, entry.response, idx)}</ResponderName>
                </ResponseHeader>
                <ResponseBody>
                  {isAnswerPublic && (() => {
                    const correctness = evaluateCorrectness(entry.response);
                    if (correctness === null) return null;
                    return (
                      <CorrectnessBadge $isCorrect={correctness} $blockSize={blockSize}>
                        {correctness ? (
                          <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style={{ strokeWidth: 3 }}>
                            <circle cx="12" cy="12" r="9" stroke="currentColor" fill="none" />
                          </svg>
                        ) : (
                          <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style={{ strokeWidth: 3 }}>
                            <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                          </svg>
                        )}
                      </CorrectnessBadge>
                    );
                  })()}
                  <ResponseText $isPlaceholder={!isResponsePublic} $blockSize={blockSize}>
                    {formatAnswer(entry.response)}
                  </ResponseText>
                </ResponseBody>
              </ResponseCard>
            );
          })}
        </ResponsesGrid>
      ) : viewMode === 'accuracy' ? (
        <>
          <AccuracyGrid $blockSize={blockSize}>
          <AccuracyCard $blockSize={blockSize}>
            <AccuracyTitle $blockSize={blockSize}>정답자</AccuracyTitle>
            <AccuracyBarWrapper>
              <AccuracyBar
                $percentage={
                  flattenedResponses.length
                    ? (accuracyStats.correct.length / flattenedResponses.length) * 100
                    : 0
                }
              />
            </AccuracyBarWrapper>
            <AccuracyCount $blockSize={blockSize}>{accuracyStats.correct.length}명</AccuracyCount>
            {isNamePublic && (
              <NameChipGroup $blockSize={blockSize}>
                {summarizeResponderNames(accuracyStats.correct).length > 0 ? (
                  summarizeResponderNames(accuracyStats.correct).map((summary) => (
                    <ResponderChip key={`correct-${summary.key}`} $blockSize={blockSize}>
                      <span>{summary.displayName}</span>
                      {summary.count > 1 && <ChipCount $blockSize={blockSize}>x{summary.count}</ChipCount>}
                    </ResponderChip>
                  ))
                ) : (
                  <NoNamesText $blockSize={blockSize}>표시할 이름 없음</NoNamesText>
                )}
              </NameChipGroup>
            )}
            {isResponsePublic && formatCorrectAnswer() && (
              <AnswerCardGroup $blockSize={blockSize}>
                <AnswerCard $isCorrect $blockSize={blockSize}>
                  {formatCorrectAnswer()}
                </AnswerCard>
              </AnswerCardGroup>
            )}
          </AccuracyCard>
          <AccuracyCard $blockSize={blockSize}>
            <AccuracyTitle $blockSize={blockSize}>오답자</AccuracyTitle>
            <AccuracyBarWrapper>
              <AccuracyBar
                $percentage={
                  flattenedResponses.length
                    ? (accuracyStats.incorrect.length / flattenedResponses.length) * 100
                    : 0
                }
                $isIncorrect
              />
            </AccuracyBarWrapper>
            <AccuracyCount $blockSize={blockSize}>{accuracyStats.incorrect.length}명</AccuracyCount>
            {isNamePublic && (
              <NameChipGroup $blockSize={blockSize}>
                {summarizeResponderNames(accuracyStats.incorrect).length > 0 ? (
                  summarizeResponderNames(accuracyStats.incorrect).map((summary) => (
                    <ResponderChip key={`incorrect-${summary.key}`} $blockSize={blockSize}>
                      <span>{summary.displayName}</span>
                      {summary.count > 1 && <ChipCount $blockSize={blockSize}>x{summary.count}</ChipCount>}
                    </ResponderChip>
                  ))
                ) : (
                  <NoNamesText $blockSize={blockSize}>표시할 이름 없음</NoNamesText>
                )}
              </NameChipGroup>
            )}
            {isResponsePublic && incorrectAnswers.length > 0 && (
              <AnswerCardGroup $blockSize={blockSize}>
                {incorrectAnswers.map((item, idx) => (
                  <AnswerCard key={`incorrect-answer-${idx}`} $isCorrect={false} $blockSize={blockSize}>
                    {item.answer}
                    {item.count > 1 && <AnswerCount $blockSize={blockSize}>x{item.count}</AnswerCount>}
                  </AnswerCard>
                ))}
              </AnswerCardGroup>
            )}
          </AccuracyCard>
        </AccuracyGrid>
        </>
      ) : (
        <>
          <BreakdownList $blockSize={blockSize}>
          {breakdownStats.map(
            (item: { label: string; count: number; responders: FlattenedResponse[] }) => {
              const percentage =
                flattenedResponses.length > 0 ? (item.count / flattenedResponses.length) * 100 : 0;

              const isCorrect = isCorrectAnswer(item.label);
              
              return (
                <BreakdownCard key={item.label} $blockSize={blockSize}>
                  <BreakdownInfo>
                    <BreakdownLabelWrapper>
                      {isAnswerPublic && (
                        <BreakdownCorrectnessBadge $isCorrect={isCorrect} $blockSize={blockSize}>
                          {isCorrect ? (
                            <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style={{ strokeWidth: 3 }}>
                              <circle cx="12" cy="12" r="9" stroke="currentColor" fill="none" />
                            </svg>
                          ) : (
                            <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style={{ strokeWidth: 3 }}>
                              <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                            </svg>
                          )}
                        </BreakdownCorrectnessBadge>
                      )}
                      <BreakdownLabel $blockSize={blockSize}>{item.label}</BreakdownLabel>
                    </BreakdownLabelWrapper>
                    <BreakdownCount $blockSize={blockSize}>{item.count}명</BreakdownCount>
                  </BreakdownInfo>
                  <BreakdownBarWrapper>
                    <BreakdownBar $percentage={percentage} />
                  </BreakdownBarWrapper>
                  {isNamePublic && (
                    <BreakdownNames $blockSize={blockSize}>
                      {summarizeResponderNames(item.responders).length > 0 ? (
                        summarizeResponderNames(item.responders).map((summary) => (
                          <ResponderChip key={`${item.label}-${summary.key}`} $blockSize={blockSize}>
                            <span>{summary.displayName}</span>
                            {summary.count > 1 && <ChipCount $blockSize={blockSize}>x{summary.count}</ChipCount>}
                          </ResponderChip>
                        ))
                      ) : (
                        <NoNamesText $blockSize={blockSize}>표시할 이름 없음</NoNamesText>
                      )}
                    </BreakdownNames>
                  )}
                </BreakdownCard>
              );
            }
          )}
        </BreakdownList>
        </>
      )}

      {showQuizDetailModal && quiz && createPortal(
        <QuizDetailModalOverlay onClick={handleCloseModal}>
          <QuizDetailModalContent onClick={(e) => e.stopPropagation()}>
            <QuizDetailHeader>
              <QuizDetailTitle>문제 상세</QuizDetailTitle>
              <CameraButtonGroup>
                <CameraButton onClick={toggleWebcam} $isActive={showWebcam} title={showWebcam ? '카메라 끄기' : '카메라 켜기'}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M23 7l-7 5 7 5V7z" strokeLinecap="round" strokeLinejoin="round"/>
                    <rect x="1" y="5" width="15" height="14" rx="2" ry="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </CameraButton>
                {showWebcam && availableCameras.length > 1 && (
                  <CameraSwitchButton onClick={switchCamera} title="카메라 전환">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M23 4v6h-6" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M1 20v-6h6" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </CameraSwitchButton>
                )}
              </CameraButtonGroup>
              <CloseButton onClick={handleCloseModal}>×</CloseButton>
            </QuizDetailHeader>
            <QuizDetailBody $hasChoices={quiz.type === 'choice' && !!quiz.choices && quiz.choices.length > 0} $showWebcam={showWebcam}>
              {showWebcam ? (
                <WebcamContainer>
                  <WebcamVideo ref={videoRef} autoPlay playsInline muted />
                </WebcamContainer>
              ) : (
                <>
                  <QuizDetailQuestion $hasChoices={quiz.type === 'choice' && !!quiz.choices && quiz.choices.length > 0}>
                    {quiz.question}
                  </QuizDetailQuestion>
                  
                  {quiz.questionImageUrl && quiz.questionImageUrl.trim() && (
                    <PresentationQuestionImageBox>
                      <PresentationQuestionImage src={quiz.questionImageUrl} alt="질문 이미지" />
                    </PresentationQuestionImageBox>
                  )}
                  
                  {quiz.type === 'choice' && quiz.choices && quiz.choices.length > 0 && (
                    <QuizDetailChoices $choiceCount={quiz.choices.length}>
                      {quiz.choices.map((choice, index) => (
                        <QuizDetailChoice key={choice.id} $choiceCount={quiz.choices!.length}>
                          <ChoiceNumber $choiceCount={quiz.choices!.length}>{index + 1}번</ChoiceNumber>
                          <ChoiceText $choiceCount={quiz.choices!.length}>{choice.text}</ChoiceText>
                        </QuizDetailChoice>
                      ))}
                    </QuizDetailChoices>
                  )}
                  
                  {(quiz.type === 'short' || quiz.type === 'essay') && quiz.guide && (
                    <QuizDetailGuide>{quiz.guide}</QuizDetailGuide>
                  )}
                </>
              )}
            </QuizDetailBody>
          </QuizDetailModalContent>
        </QuizDetailModalOverlay>,
        document.body
      )}
      <ThemeToggleWrapper>
        <ThemeToggleButton
          $isDark={themeMode === 'dark'}
          onClick={handleThemeToggle}
          aria-label="테마 전환"
        >
          <ThemeToggleOption $isActive={themeMode === 'light'} $isDark={themeMode === 'dark'}>
            일반
          </ThemeToggleOption>
          <ThemeToggleOption $isActive={themeMode === 'dark'} $isDark={themeMode === 'dark'}>
            다크
          </ThemeToggleOption>
        </ThemeToggleButton>
      </ThemeToggleWrapper>
    </PageContainer>
  );
};

const PageContainer = styled.div`
  min-height: 100vh;
  background: var(--presentation-page-bg);
  padding: 32px;
  box-sizing: border-box;
  font-family: 'Pretendard', 'Noto Sans KR', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  display: flex;
  flex-direction: column;
`;

const ThemeToggleWrapper = styled.div`
  width: 100%;
  margin-top: auto;
  padding: 24px 0 0;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ThemeToggleButton = styled.button<{ $isDark: boolean }>`
  display: flex;
  background: var(--color-accent-strong);
  border-radius: 13px;
  padding: 2px;
  position: relative;
  cursor: pointer;
  transition: all 0.3s ease;
  height: 26px;
  min-width: 80px;
  min-height: 26px;
  box-sizing: border-box;
  flex-shrink: 0;
  width: auto;
  border: none;
  appearance: none;
  -webkit-appearance: none;

  :root[data-theme='light'] & {
    background: #ffffff;
    border: 1px solid var(--color-border);
  }

  &::before {
    content: '';
    position: absolute;
    top: 2px;
    left: ${props => props.$isDark ? '50%' : '2px'};
    width: calc(50% - 2px);
    height: calc(100% - 4px);
    background: var(--color-surface);
    border-radius: 11px;
    transition: left 0.3s ease;
    z-index: 1;
  }

  :root[data-theme='light'] &::before {
    background: var(--color-accent-strong);
  }

  &:focus-visible {
    outline: 2px solid var(--color-accent-strong);
    outline-offset: 2px;
  }
`;

const ThemeToggleOption = styled.div<{ $isActive: boolean; $isDark: boolean }>`
  flex: 1;
  padding: 2px 10px;
  font-size: 11px !important;
  font-weight: 600;
  color: ${props => props.$isActive ? 'var(--color-toggle-active)' : 'var(--color-toggle-inactive)'};
  text-align: center;
  position: relative;
  z-index: 2;
  transition: color 0.3s ease;
  user-select: none;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;

  :root[data-theme='light'] & {
    color: ${props => props.$isActive ? '#ffffff' : 'rgba(var(--color-accent-rgb), 0.6)'};
  }
`;

const PresentationHeader = styled.header`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  margin-bottom: 24px;
  position: relative;
`;

const PresentationHeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  position: absolute;
  top: 0;
  right: 0;
  
  @media (max-width: 768px) {
    position: static;
    justify-content: center;
    width: 100%;
    margin-top: 12px;
  }
`;

const PresentationCloseButton = styled.button`
  width: 40px;
  height: 40px;
  min-width: 40px;
  min-height: 40px;
  border-radius: 50%;
  border: 2px solid var(--color-border);
  background: var(--color-surface);
  color: var(--color-text-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 0;
  flex-shrink: 0;
  
  svg {
    width: 20px;
    height: 20px;
    stroke: currentColor;
  }
  
  &:hover {
    background: var(--color-panel);
    border-color: var(--color-accent-strong);
    transform: scale(1.05);
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  &:focus-visible {
    outline: 2px solid var(--color-accent-strong);
    outline-offset: 2px;
  }
  
  /* 다크 모드 스타일 */
  :root:not([data-theme='light']) & {
    border-color: var(--color-border);
    color: var(--color-text-primary);
    
    &:hover {
      background: rgba(255, 255, 255, 0.1);
      border-color: #ffffff;
    }
  }
  
  /* 모바일/좁은 화면: 화면 최상단 오른쪽에 고정, 작게 표시 */
  @media (max-width: 768px) {
    position: fixed;
    top: 8px;
    right: 8px;
    z-index: 9999;
    width: 32px;
    height: 32px;
    min-width: 32px;
    min-height: 32px;
    
    svg {
      width: 16px;
      height: 16px;
    }
  }
`;

const PageTitle = styled.p`
  margin: 0;
  font-size: 16px;
  color: #6c757d;
  font-weight: 600;
  text-align: center;
`;

const QuestionTitle = styled.h1<{ $isClickable?: boolean }>`
  margin: 4px 0;
  font-size: 32px;
  font-weight: 700;
  color: var(--presentation-title-text-color);
  cursor: ${props => props.$isClickable ? 'pointer' : 'default'};
  transition: opacity 0.2s ease;
  text-align: center;
  
  &:hover {
    opacity: ${props => props.$isClickable ? 0.7 : 1};
  }
`;

const QuestionGuide = styled.p`
  margin: 0;
  color: var(--color-text-secondary);
  font-size: 16px;
`;

const PresentationHeaderContent = styled.div`
  width: 100%;
  max-width: 1200px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const OptionsSummary = styled.div`
  display: flex;
  gap: 20px;
  flex-wrap: nowrap;
  justify-content: center;
  margin-top: 32px;
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
    gap: 8px;
  }
`;

const OptionToggleBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    gap: 4px;
  }
`;

const OptionToggleLabel = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: var(--presentation-modal-label-color);
  
  @media (max-width: 768px) {
    font-size: 13px;
  }
`;

const SizeControlButtons = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
  flex-shrink: 0;
  
  @media (min-width: 769px) {
    position: absolute;
    right: 0;
  }
  
  @media (max-width: 768px) {
    justify-content: center;
    width: 100%;
    position: static;
  }
`;

const SizeControlButton = styled.button<{ disabled?: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 2px solid ${props => props.disabled ? 'var(--color-border)' : 'var(--color-accent-strong)'};
  background: ${props => props.disabled ? '#f1f3f5' : 'var(--color-surface)'};
  color: ${props => props.disabled ? 'var(--color-text-muted)' : 'var(--color-accent-strong)'};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  padding: 0;
  
  /* 다크 모드: 테두리와 아이콘을 흰색으로 변경 */
  :root:not([data-theme='light']) & {
    border: 2px solid ${props => props.disabled ? 'var(--color-border)' : '#ffffff'};
    color: ${props => props.disabled ? 'var(--color-text-muted)' : '#ffffff'};
  }
  
  &:hover:not(:disabled) {
    background: rgba(var(--color-accent-rgb), 0.1);
  }
  
  /* 다크 모드 hover */
  :root:not([data-theme='light']) &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.1);
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
  
  &:disabled {
    opacity: 0.5;
  }
  
  svg {
    width: 14px;
    height: 14px;
  }
`;

const OptionToggleButtons = styled.div<{ $isActive: boolean; $isDisabled?: boolean }>`
  display: flex;
  background: ${props => props.$isDisabled ? 'var(--presentation-toggle-disabled-bg)' : 'var(--color-accent-strong)'};
  border-radius: 11px;
  padding: 2px;
  position: relative;
  cursor: ${props => props.$isDisabled ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;
  height: 24px;
  min-width: 88px;
  width: auto;
  
  @media (max-width: 768px) {
    min-width: 72px;
    height: 22px;
    border-radius: 9px;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 2px;
    left: ${props => props.$isActive ? '50%' : '2px'};
    width: calc(50% - 2px);
    height: calc(100% - 4px);
    background: ${props => props.$isDisabled ? 'var(--presentation-toggle-disabled-slider-bg, var(--color-surface))' : 'var(--color-surface)'};
    border-radius: 9px;
    
    @media (max-width: 768px) {
      border-radius: 7px;
    }
    transition: left 0.3s ease;
    z-index: 1;
  }
  
  /* 라이트 모드에서 비활성화 슬라이더 배경색: 원래 동작 유지 (흰색) */
  :root[data-theme='light'] &::before {
    background: ${props => props.$isDisabled ? 'var(--presentation-toggle-disabled-slider-bg, var(--color-surface))' : 'var(--color-surface)'};
  }
`;

const OptionToggleButton = styled.div<{ $isActive: boolean; $isDisabled?: boolean }>`
  flex: 1;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 600;
  
  @media (max-width: 768px) {
    padding: 2px 6px;
    font-size: 10px;
  }
  color: ${props => {
    if (props.$isDisabled) {
      return 'var(--presentation-toggle-disabled-text)';
    }
    return props.$isActive ? 'var(--presentation-toggle-active-text)' : 'white';
  }};
  text-align: center;
  position: relative;
  z-index: 2;
  transition: color 0.3s ease;
  user-select: none;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
  white-space: nowrap;
  cursor: ${props => props.$isDisabled ? 'not-allowed' : 'pointer'};
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const ViewToggleRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin: 12px 0 24px;
  position: relative;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`;

const ViewToggleButtonsContainer = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 12px;
  
  @media (max-width: 768px) {
    flex-wrap: nowrap;
    gap: 6px;
  }
`;

const ViewToggleLabelFull = styled.span`
  @media (max-width: 768px) {
    display: none;
  }
`;

const ViewToggleLabelShort = styled.span`
  display: none;
  @media (max-width: 768px) {
    display: inline;
  }
`;

const ViewToggleButton = styled.button<{ $active: boolean }>`
  padding: 10px 20px;
  border-radius: 999px;
  border: 2px solid ${({ $active }) => ($active ? 'var(--color-accent-strong)' : 'var(--color-border)')};
  background: ${({ $active }) => ($active ? 'var(--color-accent-strong)' : '#ffffff')};
  color: ${({ $active }) => ($active ? '#ffffff' : 'var(--color-text-secondary)')};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  /* 다크 모드: 비선택 시 transparent, 선택 시 SortToggleButton과 동일한 스타일 */
  :root:not([data-theme='light']) & {
    background: ${({ $active }) => ($active ? 'var(--color-accent-strong)' : 'transparent')};
    border: 2px solid ${({ $active }) => ($active ? 'var(--color-accent-strong)' : 'var(--color-border)')};
    color: ${({ $active }) => ($active ? '#ffffff' : 'var(--color-text-secondary)')};
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const SortToggleRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  margin: 0 0 24px 0;
`;

const SortToggleButtons = styled.div`
  display: inline-flex;
  border: 1px solid var(--color-border);
  border-radius: 999px;
  overflow: hidden;
`;

const SortToggleButton = styled.button<{ $active: boolean; $disabled: boolean }>`
  padding: 8px 16px;
  border: none;
  background: ${({ $active, $disabled }) => 
    $disabled 
      ? 'var(--sort-button-disabled-bg)' 
      : $active 
        ? 'var(--color-accent-strong)' 
        : 'transparent'};
  color: ${({ $active, $disabled }) => 
    $disabled 
      ? 'var(--sort-button-disabled-text)' 
      : $active 
        ? '#ffffff' 
        : 'var(--color-text-secondary)'};
  font-size: 13px;
  font-weight: 600;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  transition: background 0.2s ease, color 0.2s ease;

  &:not(:last-child) {
    border-right: 1px solid var(--color-border);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const ResponsesGrid = styled.div<{ $blockSize: number }>`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(${props => 240 * props.$blockSize}px, 1fr));
  gap: ${props => 18 * props.$blockSize}px;
`;

const ResponseCard = styled.div<{ $blockSize: number }>`
  background: var(--presentation-response-card-bg);
  border-radius: ${props => 16 * props.$blockSize}px;
  padding: ${props => 18 * props.$blockSize}px;
  box-shadow: 0 16px 32px rgba(15, 61, 62, 0.08);
  min-height: ${props => 140 * props.$blockSize}px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  position: relative; /* 정답 뱃지 위치 고정 */
`;

const ResponseHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 12px;
  margin-bottom: 12px;
`;

const ResponderName = styled.span<{ $blockSize: number }>`
  font-size: ${props => 18 * props.$blockSize}px;
  font-weight: 700;
  color: var(--presentation-title-text-color);
  flex: 1;
`;

const ResponseBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CorrectnessBadge = styled.div<{ $isCorrect?: boolean; $blockSize?: number }>`
  position: absolute;
  top: ${props => 12 * (props.$blockSize || 1)}px;
  right: ${props => 12 * (props.$blockSize || 1)}px;
  width: ${props => 24 * (props.$blockSize || 1)}px;
  height: ${props => 24 * (props.$blockSize || 1)}px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $isCorrect }) => ($isCorrect ? '#10504a' : '#ff6b6b')};
  border: ${props => 2 * (props.$blockSize || 1)}px solid white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  z-index: 1;
  overflow: visible;
  padding: 2px;

  svg {
    width: ${props => 14 * (props.$blockSize || 1)}px;
    height: ${props => 14 * (props.$blockSize || 1)}px;
    color: white;
    display: block;
    overflow: visible;
  }
`;

const ResponseText = styled.p<{ $isPlaceholder: boolean; $blockSize: number }>`
  margin: 0;
  min-height: ${props => 60 * props.$blockSize}px;
  font-size: ${props => 18 * props.$blockSize}px;
  line-height: 1.4;
  color: ${({ $isPlaceholder }) => ($isPlaceholder ? 'var(--color-text-muted)' : 'var(--presentation-response-text-color)')};
  word-break: break-word;
`;

const CenteredMessage = styled.div`
  margin-top: 80px;
  text-align: center;
  font-size: 18px;
  color: #6c757d;
`;

const AccuracyGrid = styled.div<{ $blockSize: number }>`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(${props => 260 * props.$blockSize}px, 1fr));
  gap: ${props => 20 * props.$blockSize}px;
`;

const AccuracyCard = styled.div<{ $blockSize: number }>`
  background: var(--presentation-response-card-bg);
  border-radius: ${props => 16 * props.$blockSize}px;
  padding: ${props => 22 * props.$blockSize}px;
  box-shadow: 0 16px 32px rgba(15, 61, 62, 0.08);
  display: flex;
  flex-direction: column;
  gap: ${props => 12 * props.$blockSize}px;
  position: relative;
`;

const AccuracyTitle = styled.h4<{ $blockSize: number }>`
  margin: 0;
  font-size: ${props => 18 * props.$blockSize}px;
  font-weight: 700;
  color: var(--presentation-title-text-color);
`;

const AccuracyBarWrapper = styled.div`
  width: 100%;
  height: 14px;
  background: #f1f3f5;
  border-radius: 999px;
  overflow: hidden;

  /* 다크 모드: 배경을 진한 회색으로 변경 */
  :root:not([data-theme='light']) & {
    background: #404040;
  }
`;

const AccuracyBar = styled.div<{ $percentage: number; $isIncorrect?: boolean }>`
  height: 100%;
  width: ${({ $percentage }) => Math.min(100, $percentage)}%;
  background: ${({ $isIncorrect }) =>
    $isIncorrect ? 'rgba(239, 68, 68, 0.85)' : 'rgba(var(--color-accent-rgb), 0.9)'};
  border-radius: 999px;
  transition: width 0.3s ease;

  /* 다크 모드: 데이터 막대를 흰색으로 변경 */
  :root:not([data-theme='light']) & {
    background: #ffffff;
  }
`;

const AccuracyCount = styled.div<{ $blockSize: number }>`
  font-size: ${props => 16 * props.$blockSize}px;
  font-weight: 600;
`;

const NameChipGroup = styled.div<{ $blockSize?: number }>`
  display: flex;
  flex-wrap: wrap;
  gap: ${props => 6 * (props.$blockSize || 1)}px;
  min-height: ${props => 20 * (props.$blockSize || 1)}px;
`;

const ResponderChip = styled.span<{ $blockSize?: number }>`
  display: inline-flex;
  align-items: center;
  gap: ${props => 4 * (props.$blockSize || 1)}px;
  background: var(--presentation-responder-chip-bg);
  color: var(--presentation-responder-chip-text);
  border-radius: 999px;
  padding: ${props => 2 * (props.$blockSize || 1)}px ${props => 8 * (props.$blockSize || 1)}px;
  font-size: ${props => 14 * (props.$blockSize || 1)}px;
  font-weight: 500;
`;

const ChipCount = styled.span<{ $blockSize?: number }>`
  font-size: ${props => 12 * (props.$blockSize || 1)}px;
  color: var(--presentation-responder-chip-text);
  opacity: 0.7;
`;

const NoNamesText = styled.span<{ $blockSize?: number }>`
  font-size: ${props => 14 * (props.$blockSize || 1)}px;
  color: var(--color-text-muted);
`;

const AnswerCardGroup = styled.div<{ $blockSize?: number }>`
  display: flex;
  flex-wrap: wrap;
  gap: ${props => 8 * (props.$blockSize || 1)}px;
  margin-top: ${props => 8 * (props.$blockSize || 1)}px;
`;

const AnswerCard = styled.div<{ $isCorrect: boolean; $blockSize?: number }>`
  display: inline-flex;
  align-items: center;
  gap: ${props => 6 * (props.$blockSize || 1)}px;
  background: var(--presentation-response-card-bg);
  color: #212529;
  border: 1px solid var(--color-border);
  border-radius: ${props => 8 * (props.$blockSize || 1)}px;
  padding: ${props => 8 * (props.$blockSize || 1)}px ${props => 12 * (props.$blockSize || 1)}px;
  font-size: ${props => 14 * (props.$blockSize || 1)}px;
  font-weight: 600;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  /* 다크 모드: 테두리와 텍스트를 흰색으로 변경 */
  :root:not([data-theme='light']) & {
    border: 1px solid #ffffff;
    color: #ffffff;
  }
`;

const AnswerCount = styled.span<{ $blockSize?: number }>`
  font-size: ${props => 12 * (props.$blockSize || 1)}px;
  color: inherit;
  opacity: 0.7;
`;

const BreakdownList = styled.div<{ $blockSize: number }>`
  display: flex;
  flex-direction: column;
  gap: ${props => 12 * props.$blockSize}px;
`;

const BreakdownCard = styled.div<{ $blockSize: number }>`
  background: var(--presentation-response-card-bg);
  border-radius: ${props => 14 * props.$blockSize}px;
  padding: ${props => 16 * props.$blockSize}px ${props => 20 * props.$blockSize}px;
  box-shadow: 0 10px 24px rgba(var(--color-accent-rgb), 0.08);
  display: flex;
  flex-direction: column;
  gap: ${props => 10 * props.$blockSize}px;
`;

const BreakdownInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
`;

const BreakdownLabelWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
`;

const BreakdownCorrectnessBadge = styled.div<{ $isCorrect: boolean; $blockSize?: number }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${props => 20 * (props.$blockSize || 1)}px;
  height: ${props => 20 * (props.$blockSize || 1)}px;
  border-radius: 50%;
  background: ${({ $isCorrect }) => ($isCorrect ? '#10504a' : '#ff6b6b')};
  border: ${props => 2 * (props.$blockSize || 1)}px solid white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  flex-shrink: 0;
  overflow: visible;
  padding: 2px;
  
  svg {
    width: ${props => 12 * (props.$blockSize || 1)}px;
    height: ${props => 12 * (props.$blockSize || 1)}px;
    color: white;
    display: block;
    overflow: visible;
  }
`;

const BreakdownLabel = styled.span<{ $blockSize: number }>`
  font-size: ${props => 16 * props.$blockSize}px;
  font-weight: 600;
  color: var(--presentation-title-text-color);
`;

const BreakdownCount = styled.span<{ $blockSize: number }>`
  font-size: ${props => 16 * props.$blockSize}px;
  font-weight: 600;
  color: var(--presentation-accent-text-color);
`;

const BreakdownBarWrapper = styled.div`
  width: 100%;
  height: 10px;
  background: #f1f3f5;
  border-radius: 999px;

  /* 다크 모드: 배경을 진한 회색으로 변경 */
  :root:not([data-theme='light']) & {
    background: #404040;
  }
`;

const BreakdownBar = styled.div<{ $percentage: number }>`
  height: 100%;
  width: ${({ $percentage }) => Math.min(100, $percentage)}%;
  background: rgba(var(--color-accent-rgb), 0.85);
  border-radius: 999px;

  /* 다크 모드: 데이터 막대를 흰색으로 변경 */
  :root:not([data-theme='light']) & {
    background: #ffffff;
  }
`;

const BreakdownNames = styled(NameChipGroup)`
  margin-top: ${props => 8 * (props.$blockSize || 1)}px;
`;

const QuizDetailModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 20px;
  box-sizing: border-box;
`;

const QuizDetailModalContent = styled.div`
  background: var(--color-surface);
  border-radius: 16px;
  width: 85vw;
  height: 85vh;
  display: flex;
  flex-direction: column;
  border: var(--modal-shell-border);
  box-shadow: var(--modal-shadow-xlarge);
  overflow: hidden;
`;

const QuizDetailHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 16px;
  border-bottom: 1px solid var(--color-border);
  background: var(--color-accent-strong);
  color: white;
  flex-shrink: 0;
  position: relative;
`;

const QuizDetailTitle = styled.h2`
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: white;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 22px;
  color: white;
  cursor: pointer;
  padding: 0;
  width: 28px;
  height: 28px;
  min-width: 28px;
  min-height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: background 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const CameraButtonGroup = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CameraButton = styled.button<{ $isActive: boolean }>`
  background: ${props => props.$isActive ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0.15)'};
  border: none;
  width: 32px;
  height: 32px;
  min-width: 32px;
  min-height: 32px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
  
  svg {
    width: 18px;
    height: 18px;
    min-width: 18px;
    min-height: 18px;
    stroke: white;
  }
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: scale(1.05);
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const CameraSwitchButton = styled.button`
  background: rgba(255, 255, 255, 0.15);
  border: none;
  width: 32px;
  height: 32px;
  min-width: 32px;
  min-height: 32px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
  
  svg {
    width: 16px;
    height: 16px;
    min-width: 16px;
    min-height: 16px;
    stroke: white;
  }
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: scale(1.05);
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const WebcamContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #000;
  overflow: hidden;
`;

const WebcamVideo = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
  background: #000;
`;

const QuizDetailBody = styled.div<{ $hasChoices: boolean; $showWebcam?: boolean }>`
  padding: ${props => props.$showWebcam ? '0' : 'clamp(24px, 3vw, 40px)'};
  overflow: hidden;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${props => props.$hasChoices ? 'clamp(12px, 1.5vw, 20px)' : 'clamp(16px, 2vw, 24px)'};
  justify-content: ${props => props.$hasChoices ? 'flex-start' : 'center'};
  align-items: ${props => props.$hasChoices ? 'stretch' : 'center'};
  text-align: ${props => props.$hasChoices ? 'left' : 'center'};
  min-height: 0;
  position: relative;
`;

const QuizDetailQuestion = styled.h3<{ $hasChoices: boolean }>`
  margin: 0;
  font-size: ${props => props.$hasChoices 
    ? 'clamp(22px, 2.8vw, 36px)' 
    : 'clamp(40px, 5vw, 72px)'};
  font-weight: 700;
  color: var(--presentation-title-text-color);
  line-height: 1.3;
  flex-shrink: 0;
`;

const PresentationQuestionImageBox = styled.div`
  width: 100%;
  max-width: 800px;
  aspect-ratio: 16 / 9;
  border: 2px dashed var(--color-border);
  border-radius: 12px;
  background: var(--color-panel);
  overflow: hidden;
  position: relative;
  margin: 20px auto;
  align-self: center;
`;

const PresentationQuestionImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const PresentationQuestionImageBoxSmall = styled.div`
  width: 100%;
  max-width: 400px;
  aspect-ratio: 16 / 9;
  border: 2px dashed var(--color-border);
  border-radius: 12px;
  background: var(--color-panel);
  overflow: hidden;
  position: relative;
  margin: 12px auto;
  align-self: center;
`;

const PresentationQuestionImageSmall = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const QuizDetailChoices = styled.div<{ $choiceCount: number }>`
  display: flex;
  flex-direction: column;
  gap: ${props => {
    const count = props.$choiceCount;
    if (count <= 2) return 'clamp(8px, 1vw, 12px)';
    if (count <= 4) return 'clamp(6px, 0.8vw, 10px)';
    if (count <= 6) return 'clamp(4px, 0.6vw, 8px)';
    return 'clamp(3px, 0.5vw, 6px)';
  }};
  margin-top: 0;
  flex: 1;
  justify-content: center;
  min-height: 0;
  overflow: hidden;
`;

const QuizDetailChoice = styled.div<{ $choiceCount: number }>`
  display: flex;
  align-items: center;
  gap: ${props => {
    const count = props.$choiceCount;
    if (count <= 2) return 'clamp(12px, 1.5vw, 18px)';
    if (count <= 4) return 'clamp(8px, 1vw, 12px)';
    if (count <= 6) return 'clamp(6px, 0.8vw, 10px)';
    return 'clamp(4px, 0.6vw, 8px)';
  }};
  padding: ${props => {
    const count = props.$choiceCount;
    if (count <= 2) return 'clamp(8px, 1vw, 12px) clamp(12px, 1.5vw, 18px)';
    if (count <= 4) return 'clamp(6px, 0.8vw, 10px) clamp(10px, 1.2vw, 14px)';
    if (count <= 6) return 'clamp(4px, 0.6vw, 8px) clamp(8px, 1vw, 12px)';
    return 'clamp(3px, 0.5vw, 6px) clamp(6px, 0.8vw, 10px)';
  }};
  background: var(--color-panel);
  border-radius: 8px;
  border: 1px solid var(--color-border);
  flex-shrink: 0;
  min-height: 0;
`;

const ChoiceNumber = styled.div<{ $choiceCount: number }>`
  min-width: ${props => {
    const count = props.$choiceCount;
    if (count <= 2) return 'clamp(50px, 6vw, 70px)';
    if (count <= 4) return 'clamp(45px, 5.5vw, 65px)';
    if (count <= 6) return 'clamp(40px, 5vw, 60px)';
    return 'clamp(35px, 4.5vw, 55px)';
  }};
  font-size: ${props => {
    const count = props.$choiceCount;
    if (count <= 2) return 'clamp(16px, 2vw, 24px)';
    if (count <= 4) return 'clamp(14px, 1.8vw, 22px)';
    if (count <= 6) return 'clamp(12px, 1.5vw, 20px)';
    return 'clamp(11px, 1.4vw, 18px)';
  }};
  font-weight: 700;
  color: var(--color-accent-text);
  text-align: center;
  padding: ${props => {
    const count = props.$choiceCount;
    if (count <= 2) return 'clamp(6px, 0.8vw, 10px) clamp(10px, 1.2vw, 14px)';
    if (count <= 4) return 'clamp(5px, 0.6vw, 8px) clamp(8px, 1vw, 12px)';
    if (count <= 6) return 'clamp(4px, 0.5vw, 7px) clamp(6px, 0.8vw, 10px)';
    return 'clamp(3px, 0.4vw, 6px) clamp(5px, 0.6vw, 8px)';
  }};
  background: rgba(var(--color-accent-rgb), 0.1);
  border-radius: 6px;
  flex-shrink: 0;
`;

const ChoiceText = styled.div<{ $choiceCount: number }>`
  flex: 1;
  font-size: ${props => {
    const count = props.$choiceCount;
    if (count <= 2) return 'clamp(16px, 2vw, 24px)';
    if (count <= 4) return 'clamp(14px, 1.8vw, 22px)';
    if (count <= 6) return 'clamp(12px, 1.5vw, 20px)';
    return 'clamp(11px, 1.4vw, 18px)';
  }};
  color: #212529;
  line-height: 1.3;
  min-width: 0;
`;

const QuizDetailGuide = styled.div`
  padding: clamp(24px, 3vw, 40px);
  background: #f1f3f5;
  border-radius: 16px;
  font-size: clamp(28px, 3.5vw, 48px);
  color: var(--color-text-muted);
  line-height: 1.6;
  margin-top: 8px;
  width: 100%;
  box-sizing: border-box;
`;

// 자리 배치 모드 스타일 컴포넌트
const SeatModeContainer = styled.div`
  padding: 0 20px 80px;
  margin-top: 0;
  width: 100%;
`;

const SeatModeScrollArea = styled.div`
  width: 100%;
  min-width: 0;
  background: transparent;
  border: none;
  border-radius: 8px;
  padding: 2px;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
`;

const SeatModeGridWrapper = styled.div`
  min-width: 1245px;
  width: 100%;
  display: flex;
  justify-content: flex-start;
`;

const SeatGridContent = styled.div<{ $blockSize?: number }>`
  display: grid;
  gap: ${props => 12 * (props.$blockSize || 1)}px;
  width: 100%;
  min-width: 1245px;
`;

const RotateButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 16px;
  margin-bottom: 8px;
`;

const RotateButton = styled.button`
  width: 36px;
  height: 36px;
  border: 2px solid var(--color-border);
  border-radius: 50%;
  background: var(--color-surface);
  color: var(--color-text-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 0;

  &:hover {
    border-color: var(--color-accent-strong);
    background: var(--color-accent-soft);
    transform: scale(1.1);
  }

  &:active {
    transform: scale(0.95);
  }

`;

const RotateIconWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const RotateArrowIcon = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 1;
  transform: scale(1);
  transition: opacity 0.3s ease, transform 0.3s ease;

  svg {
    width: 22px;
    height: 22px;
  }

  ${RotateButton}:hover & {
    opacity: 0;
    transform: scale(0.8);
  }
`;

const RotateNumberIcon = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 700;
  color: currentColor;
  opacity: 0;
  transform: scale(0.8);
  transition: opacity 0.3s ease, transform 0.3s ease;

  ${RotateButton}:hover & {
    opacity: 1;
    transform: scale(1);
  }
`;

const GuestSection = styled.div`
  margin-top: 20px;
  padding: 20px;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 8px;
`;

const GuestSectionTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: var(--color-text-primary);
  margin-bottom: 16px;
`;

export default PresentationPage;

